import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useClient, useQuery } from 'urql';
import { z } from 'zod';
import { ChevronDown, CopyPlus, Pencil, Plus, ScanText, Trash2, X } from 'lucide-react';
import OperatorModePalette from '@/modules/guias/components/OperatorModePalette.jsx';
import useOperatorMode from '@/modules/guias/hooks/useOperatorMode.js';
import Swal from 'sweetalert2';
import Button from '@/components/ui/button.jsx';
import Input from '@/components/ui/input.jsx';
import Select from '@/components/ui/select.jsx';
import Combobox from '@/components/ui/Combobox.jsx';
import NumberInput from '@/components/ui/NumberInput.jsx';
import Textarea from '@/components/ui/textarea.jsx';
import Field from '@/components/common/Field.jsx';
import EmptyState from '@/components/common/EmptyState.jsx';
import ClienteSelectWithModal from '@/components/guias/ClienteSelectWithModal.jsx';
import useGuiaForm from './hooks/useGuiaForm.js';
import { emitirGuia } from './api/guiasApi.js';
import { useAuth } from '@/state/auth/AuthContext.jsx';
import { hasPermission } from '@/utils/permissions.js';
import { LINEAS_QUERY } from '@/graphql/lineas.gql.js';
import { RETORNABLES_TIPOS_QUERY } from '@/graphql/retornables.gql.js';
import { simulateTariff } from '@/services/tariffWorkbench.js';
import { useUI } from '@/context/UIContext.jsx';
import { previewRoutingGuia } from '@/services/sucursalesOperativas.js';
import { applyNumeroRemitoInput, applyPuntoVentaInput } from './remitoInput.js';
import { Q_CLIENTE, Q_CLIENTE_SUBCUENTAS_ACTIVAS } from '@/modules/administracion/clientes/clientes.gql.js';
import { getLocalities, getProvinces } from '@/services/clients.js';
import { printGuiaDirectly } from './services/printGuia.js';
import { getLocalPrintingPreferences } from '@/services/printing/directPrintService.js';
import { mapEtiquetaOrigenToGuia } from './etiquetaOrigen.js';
import { formatLineaLabel } from './lineaLabel.js';

const DRAFT_KEY = 'emision-guia-v3-draft';
const DRAFT_TTL_MS = 1000 * 60 * 30;

const clearGuiaDraftStorage = () => {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(DRAFT_KEY);
};

const buildDraftSessionKey = (user, sucursalId) => `${user?.id || 'anon'}:${sucursalId || '0'}`;

const remitoDefault = {
  punto_venta: '',
  numero_remito: '',
  bultos: '',
  kg_reales: '',
  m3: '',
  kg_aforado: '',
  valor_declarado: '',
  descripcion: '',
};

const numericFieldRules = {
  bultos: { decimals: 0 },
  kg_reales: { decimals: 3 },
  m3: { decimals: 4 },
  kg_aforado: { decimals: 3 },
  valor_declarado: { decimals: 2 },
};

const remitoDescriptionOptions = [
  'Bultos',
  'Cajas',
  'Pallets',
  'Mercaderia general',
];

const formatCurrency = value => new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(Number(value || 0));

const toNullableString = value => {
  if (value === null || value === undefined) return '';
  return String(value).trim();
};

const toNumber = (value, fallback = 0) => {
  if (value === '' || value === null || value === undefined) return fallback;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const parseJsonSafe = value => {
  if (!value) return null;
  if (typeof value === 'object') return value;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const toPositiveIntOrUndefined = value => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return undefined;
  return Math.trunc(parsed);
};

const resolveAutoPagadorId = (modalidadCobro, remitenteId, destinatarioId) => {
  const modalidad = String(modalidadCobro || '').toLowerCase();
  if (modalidad.includes('origen')) return toNullableString(remitenteId);
  if (modalidad.includes('destino')) return toNullableString(destinatarioId);
  return '';
};

const resolveClienteValorizacionId = ({ clientePagadorId, modalidadCobro, remitenteId, destinatarioId }) => (
  toNullableString(clientePagadorId)
  || resolveAutoPagadorId(modalidadCobro, remitenteId, destinatarioId)
);

const buildTariffSimulationBody = ({
  fecha,
  clienteId,
  sucursalId,
  routingPreview,
  localidadDestinoId,
  totals,
  contrareembolso,
  versionId,
}) => ({
  fecha,
  clienteId: Number(clienteId),
  versionId: versionId || undefined,
  origenSucursalId: Number(sucursalId),
  destinoSucursalId: Number(routingPreview?.sucursal_destino_id || 0) || null,
  destinoLocalidadId: Number(localidadDestinoId || 0) || null,
  kg: Number(totals.kgReales || 0),
  bultos: Number(totals.bultos || 0),
  m3: Number(totals.m3 || 0),
  kgAforado: Number(totals.kgAforado || 0),
  valorDeclarado: Number(totals.valorDeclarado || 0),
  contrareembolso: Number(contrareembolso || 0),
});

const adaptTariffSimulation = result => {
  const detalles = Array.isArray(result?.detalles) ? result.detalles : [];

  return {
    importe_sugerido: Number(result?.totalFinal || 0),
    desglose: JSON.stringify(
      detalles.map(item => ({
        codigo: item.conceptCode || item.ruleCode || `CONCEPTO_${item.orden || ''}`.trim(),
        nombre: item.conceptName || item.conceptCode || 'Concepto valorizado',
        tipo_regla: item.ruleCode || item.metodoCalculo || 'REGLA',
        importe: Number(item.importe || 0),
        base: item.baseValor,
        porcentaje: item.porcentaje,
        valor: item.valorUnitario,
        regla_id: item.ruleId,
        regla_codigo: item.ruleCode,
        detalle: `${item.conceptCode || 'CONCEPTO'} · ${item.ruleCode || 'sin regla'} · ${item.baseCodigo || 'BASE'} ${item.baseValor ?? ''}`.trim(),
      })),
    ),
    simulation: result,
    version_principal: result?.versionPrincipal || null,
    versiones_candidatas: Array.isArray(result?.versionesCandidatas) ? result.versionesCandidatas : [],
  };
};

const VALUATION_MODE = {
  AUTO: 'AUTO',
  MANUAL: 'MANUAL',
  OVERRIDE: 'OVERRIDE',
  SIN_VALORIZAR: 'SIN_VALORIZAR',
};

const formatVersionOptionLabel = version => {
  if (!version) return 'Version sin dato';
  const lista = toNullableString(version.listaCodigo || version.listaNombre);
  const numero = toNullableString(version.versionNumero || version.numero || version.id);
  return `${lista || 'Lista'} · v${numero || '-'}`;
};

const buildManualValuationItems = ({ amount, reason, origin }) => ([{
  codigo: origin === 'override' ? 'OVERRIDE_MANUAL' : 'MANUAL',
  nombre: origin === 'override' ? 'Override manual' : 'Carga manual',
  tipo_regla: origin === 'override' ? 'OVERRIDE' : 'MANUAL',
  importe: Number(amount || 0),
  base: 1,
  porcentaje: 0,
  valor: Number(amount || 0),
  detalle: reason || (origin === 'override' ? 'Override manual' : 'Carga manual'),
  origen: origin,
}]);

const buildValuationSnapshotForDecision = ({
  mode,
  pendingPayload,
  tarifaPreview,
  detalleTarifaPreview,
  tarifaPreviewWarnings,
  clienteValorizacionId,
  manualAmount,
  manualReason,
  selectedVersion,
}) => {
  const suggestedTotal = Number(tarifaPreview?.importe_sugerido || pendingPayload?.total_valorizado || 0);
  const selectedVersionLabel = formatVersionOptionLabel(selectedVersion || tarifaPreview?.version_principal || null);

  if (mode === VALUATION_MODE.SIN_VALORIZAR) {
    return {
      total_valorizado: 0,
      valorizada: false,
      detalle_valorizacion_json: {
        version: 2,
        origen: 'emision_preview',
        source: 'EMISION',
        cliente_id: Number(clienteValorizacionId || 0) || null,
        version_principal: null,
        items: [],
        warnings: [...tarifaPreviewWarnings, 'Emision sin valorizar. Facturacion y cobranza quedan bloqueadas hasta valorizar.'],
        valuation_decision: {
          mode,
          origin: 'sin_valorizar',
          pending_valuation: true,
          selected_by_user: true,
          selected_version_id: null,
          selected_version_label: null,
          reason: null,
        },
      },
    };
  }

  if (mode === VALUATION_MODE.MANUAL || mode === VALUATION_MODE.OVERRIDE) {
    const origin = mode === VALUATION_MODE.OVERRIDE ? 'override' : 'manual';
    const amount = Number(manualAmount || 0);
    return {
      total_valorizado: amount,
      valorizada: amount > 0,
      detalle_valorizacion_json: {
        version: 2,
        origen: 'emision_preview',
        source: 'EMISION',
        cliente_id: Number(clienteValorizacionId || 0) || null,
        version_principal: null,
        items: buildManualValuationItems({ amount, reason: manualReason, origin }),
        warnings: [],
        valuation_decision: {
          mode,
          origin,
          pending_valuation: false,
          selected_by_user: true,
          selected_version_id: null,
          selected_version_label: null,
          reason: toNullableString(manualReason),
        },
      },
    };
  }

  return {
    total_valorizado: suggestedTotal,
    valorizada: suggestedTotal > 0,
    detalle_valorizacion_json: {
      version: 2,
      origen: 'emision_preview',
      source: 'EMISION',
      cliente_id: Number(clienteValorizacionId || 0) || null,
      version_principal: tarifaPreview?.version_principal || null,
      items: detalleTarifaPreview,
      warnings: tarifaPreviewWarnings,
      valuation_decision: {
        mode,
        origin: 'automatica',
        pending_valuation: false,
        selected_by_user: true,
        selected_version_id: selectedVersion?.id || tarifaPreview?.version_principal?.id || null,
        selected_version_label: selectedVersionLabel,
        reason: null,
      },
    },
  };
};

const optionalPositiveIntSchema = z.preprocess(
  value => {
    const normalized = toPositiveIntOrUndefined(value);
    return normalized === undefined ? undefined : normalized;
  },
  z.number().int().positive('punto_venta_id es obligatorio').optional(),
);

const buildClienteSnapshot = (cliente, fallback = {}) => {
  const nombre = toNullableString(
    cliente?.razonSocial
    || cliente?.razon_social
    || cliente?.nombre
    || cliente?.nombreFantasia
    || cliente?.nombre_fantasia
    || fallback.nombre,
  );

  return {
    nombre,
    direccion: toNullableString(
      cliente?.direccion
      || cliente?.domicilio
      || fallback.direccion,
    ),
    localidad: toNullableString(
      cliente?.localidad?.nombre
      || cliente?.localidadNombre
      || cliente?.localidad_nombre
      || cliente?.localidad
      || fallback.localidad,
    ),
    provincia: toNullableString(
      cliente?.provincia?.nombre
      || cliente?.provinciaNombre
      || cliente?.provincia_nombre
      || cliente?.provincia
      || fallback.provincia,
    ),
    telefono: toNullableString(
      cliente?.telefono
      || cliente?.celular
      || fallback.telefono,
    ),
    contacto: toNullableString(
      cliente?.contacto
      || cliente?.contacto_principal
      || fallback.contacto,
    ),
  };
};

const resolveClienteLocalidadNombre = cliente => toNullableString(
  cliente?.localidad?.nombre
  || cliente?.localidadNombre
  || cliente?.localidad_nombre
  || cliente?.localidad,
);

const resolveClienteProvinciaNombre = cliente => toNullableString(
  cliente?.provincia?.nombre
  || cliente?.provinciaNombre
  || cliente?.provincia_nombre
  || cliente?.provincia,
);

const resolveClienteLocalidadId = cliente => toNullableString(
  cliente?.localidadId
  || cliente?.localidad_id
  || cliente?.domicilios?.find(item => item?.esDefault && item?.activo !== false)?.localidadId
  || cliente?.domicilios?.find(item => item?.activo !== false)?.localidadId
  || cliente?.localidad?.id,
);

const resolveClienteProvinciaId = cliente => toNullableString(
  cliente?.provinciaId
  || cliente?.provincia_id
  || cliente?.domicilios?.find(item => item?.esDefault && item?.activo !== false)?.provinciaId
  || cliente?.domicilios?.find(item => item?.activo !== false)?.provinciaId
  || cliente?.provincia?.id,
);

const normalizeGeoName = value => String(value || '')
  .trim()
  .toLowerCase()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '');

const findOptionByName = (options, value) => {
  const normalizedTarget = normalizeGeoName(value);
  if (!normalizedTarget) return null;
  return options.find(option => normalizeGeoName(option?.nombre || option?.name) === normalizedTarget) || null;
};

const resolveClienteDireccion = cliente => toNullableString(
  cliente?.direccion
  || cliente?.domicilio
  || cliente?.domicilios?.find(item => item?.esDefault && item?.activo !== false)?.direccion
  || cliente?.domicilios?.find(item => item?.activo !== false)?.direccion,
);

const resolveClienteContactoPrincipal = cliente => (
  cliente?.contactos?.find(item => item?.principal && item?.activo !== false)
  || cliente?.contactos?.find(item => item?.activo !== false)
  || null
);

const resolveClienteTelefono = cliente => toNullableString(
  cliente?.telefono
  || cliente?.celular
  || resolveClienteContactoPrincipal(cliente)?.telefono
  || resolveClienteContactoPrincipal(cliente)?.celular,
);

const applyClienteToRemitente = (setValue, cliente) => {
  const contacto = resolveClienteContactoPrincipal(cliente);
  setValue('domicilio_remitente', resolveClienteDireccion(cliente), { shouldValidate: false });
  setValue('localidad_remitente', resolveClienteLocalidadNombre(cliente), { shouldValidate: false });
  setValue('provincia_remitente', resolveClienteProvinciaNombre(cliente), { shouldValidate: false });
  setValue('telefono_remitente', resolveClienteTelefono(cliente), { shouldValidate: false });
  setValue('contacto_remitente', contacto?.nombre || '', { shouldValidate: false });
};

const applyClienteToDestinatario = (setValue, cliente) => {
  const contacto = resolveClienteContactoPrincipal(cliente);
  setValue('domicilio_destinatario', resolveClienteDireccion(cliente), { shouldValidate: false });
  setValue('localidad_destinatario', resolveClienteLocalidadNombre(cliente), { shouldValidate: false });
  setValue('provincia_destinatario', resolveClienteProvinciaNombre(cliente), { shouldValidate: false });
  setValue('telefono_destinatario', resolveClienteTelefono(cliente), { shouldValidate: false });
  setValue('contacto_destinatario', contacto?.nombre || '', { shouldValidate: false });
  setValue('localidad_destino_id', resolveClienteLocalidadId(cliente), { shouldValidate: true });
  setValue('sucursal_destino_id', '', { shouldValidate: false });
  setValue('sucursal_via_id', '', { shouldValidate: false });
};

const snapshotSchema = z.object({
  nombre: z.string().trim().min(1, 'nombre es obligatorio'),
  direccion: z.string().optional(),
  localidad: z.string().optional(),
  provincia: z.string().optional(),
  telefono: z.string().optional(),
  contacto: z.string().optional(),
});

const submitRemitoSchema = z.object({
  numero_remito: z.string().min(1, 'El número de remito es obligatorio'),
  bultos: z.coerce.number().min(0, 'Bultos debe ser mayor o igual a 0'),
  kg_reales: z.coerce.number().min(0, 'Kg reales debe ser mayor o igual a 0'),
  m3: z.coerce.number().min(0, 'M3 debe ser mayor o igual a 0'),
  kg_aforado: z.coerce.number().min(0, 'Kg aforado debe ser mayor o igual a 0'),
  valor_declarado: z.coerce.number().min(0, 'Valor declarado debe ser mayor o igual a 0'),
  descripcion: z.string().min(1, 'La descripción del remito es obligatoria'),
});

const submitPayloadSchema = z.object({
  linea_id: z.coerce.number().int().positive('linea_id es obligatorio'),
  sucursal_id: z.coerce.number().int().positive('sucursal_id es obligatorio'),
  sucursal_origen_id: z.coerce.number().int().positive('sucursal_origen_id es obligatorio'),
  sucursal_destino_id: z.string().optional(),
  sucursal_via_id: z.string().optional(),
  punto_venta_id: optionalPositiveIntSchema,
  retiro_id: optionalPositiveIntSchema,
  etiqueta_lote_id: optionalPositiveIntSchema,
  linea_numero: z.string().min(1, 'linea_numero es obligatorio'),
  fecha_emision: z.string().min(1, 'fecha_emision es obligatorio'),
  hora_emision: z.string().optional(),
  observaciones: z.string().optional(),
  modalidad_cobro: z.string().min(1, 'modalidad_cobro es obligatorio'),
  tipo_entrega: z.string().min(1, 'tipo_entrega es obligatorio'),
  remitente_id: z.string().min(1, 'remitente_id es obligatorio'),
  destinatario_id: z.string().min(1, 'destinatario_id es obligatorio'),
  cliente_pagador_id: z.string().optional(),
  subcuenta_id: z.string().optional(),
  domicilio_remitente: z.string().optional(),
  localidad_remitente: z.string().optional(),
  provincia_remitente: z.string().optional(),
  telefono_remitente: z.string().optional(),
  contacto_remitente: z.string().optional(),
  domicilio_destinatario: z.string().optional(),
  localidad_destinatario: z.string().optional(),
  provincia_destinatario: z.string().optional(),
  localidad_destino_id: z.string().optional(),
  telefono_destinatario: z.string().optional(),
  contacto_destinatario: z.string().optional(),
  contrareembolso: z.coerce.number().min(0, 'contrareembolso debe ser mayor o igual a 0'),
  total_valorizado: z.coerce.number().min(0, 'total_valorizado debe ser mayor o igual a 0'),
  valorizada: z.boolean().optional(),
  remitente_snapshot: snapshotSchema,
  destinatario_snapshot: snapshotSchema,
  remitos: z.array(submitRemitoSchema).min(1, 'Debe agregar al menos un remito'),
}).passthrough();

const formatIssuePath = path => (Array.isArray(path) && path.length ? path.join('.') : 'payload');

const resolveLineaSeleccionada = (lineas = [], lineaSeleccionada) => {
  if (!lineaSeleccionada) return null;
  if (typeof lineaSeleccionada === 'object') return lineaSeleccionada;
  return lineas.find(item => (
    String(item.id) === String(lineaSeleccionada)
    || String(item.codigo) === String(lineaSeleccionada)
  )) || null;
};

const normalizePayloadForSubmit = (raw, user, sucursalOperativa, clientesRecords, lineaSeleccionada) => {
  const lineaId = toNumber(lineaSeleccionada?.id ?? raw.linea_id ?? raw.linea_numero, 0);
  const sucursalLineaId = toNumber(lineaSeleccionada?.sucursal_id, 0);
  const sucursalUsuarioId = toNumber(
    sucursalOperativa?.id
    || sucursalOperativa?.sucursal_id
    || user?.sucursal_activa_id
    || user?.sucursal_id
    || user?.sucursal?.id,
    0,
  );
  const sucursalPayloadId = toNumber(raw.sucursal_id || raw.sucursal_origen_id, 0);
  const sucursalDestino = toNullableString(raw.sucursal_destino_id);
  const sucursalVia = toNullableString(raw.sucursal_via_id);
  const sucursalId = sucursalLineaId || sucursalPayloadId || sucursalUsuarioId;
  const puntoVentaId = toPositiveIntOrUndefined(raw.punto_venta_id);

  const normalized = {
    ...raw,
    estado: 'EMITIDA',
    linea_id: Number(lineaSeleccionada?.id ?? lineaId),
    linea_numero: toNullableString(raw.linea_numero || lineaId),
    fecha_emision: toNullableString(raw.fecha_emision || new Date().toISOString().slice(0, 10)),
    hora_emision: toNullableString(raw.hora_emision || new Date().toISOString().slice(11, 19)),
    sucursal_id: sucursalId,
    sucursal_origen_id: sucursalId,
    sucursal_destino_id: sucursalDestino,
    sucursal_via_id: sucursalVia,
    localidad_destino_id: toNullableString(raw.localidad_destino_id),
    punto_venta_id: puntoVentaId,
    retiro_id: toPositiveIntOrUndefined(raw.retiro_id),
    etiqueta_lote_id: toPositiveIntOrUndefined(raw.etiqueta_lote_id),
    modalidad_cobro: toNullableString(raw.modalidad_cobro),
    tipo_entrega: toNullableString(raw.tipo_entrega),
    observaciones: toNullableString(raw.observaciones),
    domicilio_remitente: toNullableString(raw.domicilio_remitente),
    localidad_remitente: toNullableString(raw.localidad_remitente),
    provincia_remitente: toNullableString(raw.provincia_remitente),
    telefono_remitente: toNullableString(raw.telefono_remitente),
    contacto_remitente: toNullableString(raw.contacto_remitente),
    domicilio_destinatario: toNullableString(raw.domicilio_destinatario),
    localidad_destinatario: toNullableString(raw.localidad_destinatario),
    provincia_destinatario: toNullableString(raw.provincia_destinatario),
    telefono_destinatario: toNullableString(raw.telefono_destinatario),
    contacto_destinatario: toNullableString(raw.contacto_destinatario),
    remitente_id: toNullableString(raw.remitente_id),
    destinatario_id: toNullableString(raw.destinatario_id),
    subcuenta_id: toNullableString(raw.subcuenta_id),
    contrareembolso: toNumber(raw.contrareembolso, 0),
    total_valorizado: toNumber(raw.total_valorizado, 0),
    remitos: (raw.remitos || []).map(remito => ({
      ...remito,
      numero_remito: toNullableString(remito?.numero_remito),
      bultos: toNumber(remito?.bultos, 0),
      kg_reales: toNumber(remito?.kg_reales, 0),
      m3: toNumber(remito?.m3, 0),
      kg_aforado: toNumber(remito?.kg_aforado, 0),
      valor_declarado: toNumber(remito?.valor_declarado, 0),
      descripcion: toNullableString(remito?.descripcion),
    })),
  };

  const remitenteSeleccionado = clientesRecords.find(item => String(item.id) === String(normalized.remitente_id));
  const destinatarioSeleccionado = clientesRecords.find(item => String(item.id) === String(normalized.destinatario_id));
  normalized.cliente_pagador_id = toNullableString(
    raw.cliente_pagador_id
    || resolveAutoPagadorId(normalized.modalidad_cobro, normalized.remitente_id, normalized.destinatario_id),
  );
  const pagadorSeleccionado = clientesRecords.find(item => String(item.id) === String(normalized.cliente_pagador_id));

  normalized.remitente_snapshot = buildClienteSnapshot(remitenteSeleccionado, {
    nombre: '',
    direccion: normalized.domicilio_remitente,
    localidad: normalized.localidad_remitente,
    provincia: normalized.provincia_remitente,
    telefono: normalized.telefono_remitente,
    contacto: normalized.contacto_remitente,
  });
  normalized.destinatario_snapshot = buildClienteSnapshot(destinatarioSeleccionado, {
    nombre: '',
    direccion: normalized.domicilio_destinatario,
    localidad: normalized.localidad_destinatario,
    provincia: normalized.provincia_destinatario,
    telefono: normalized.telefono_destinatario,
    contacto: normalized.contacto_destinatario,
  });
  normalized.cliente_pagador_snapshot = pagadorSeleccionado
    ? buildClienteSnapshot(pagadorSeleccionado, { nombre: '' })
    : undefined;

  normalized.domicilio_remitente = normalized.domicilio_remitente || toNullableString(normalized.remitente_snapshot?.direccion);
  normalized.localidad_remitente = normalized.localidad_remitente || toNullableString(normalized.remitente_snapshot?.localidad);
  normalized.provincia_remitente = normalized.provincia_remitente || toNullableString(normalized.remitente_snapshot?.provincia);
  normalized.telefono_remitente = normalized.telefono_remitente || toNullableString(normalized.remitente_snapshot?.telefono);
  normalized.contacto_remitente = normalized.contacto_remitente || toNullableString(normalized.remitente_snapshot?.contacto);
  normalized.domicilio_destinatario = normalized.domicilio_destinatario || toNullableString(normalized.destinatario_snapshot?.direccion);
  normalized.localidad_destinatario = normalized.localidad_destinatario || toNullableString(normalized.destinatario_snapshot?.localidad);
  normalized.provincia_destinatario = normalized.provincia_destinatario || toNullableString(normalized.destinatario_snapshot?.provincia);
  normalized.telefono_destinatario = normalized.telefono_destinatario || toNullableString(normalized.destinatario_snapshot?.telefono);
  normalized.contacto_destinatario = normalized.contacto_destinatario || toNullableString(normalized.destinatario_snapshot?.contacto);

  return normalized;
};

const normalizeNumericInput = (value, decimals) => {
  if (value === '' || value === null || value === undefined) return '';
  const normalized = String(value).replace(',', '.').replace(/[^\d.]/g, '');
  const [intPart = '', ...rest] = normalized.split('.');
  const decimalPart = rest.join('');
  const safeInt = intPart.replace(/^0+(\d)/, '$1') || (normalized.startsWith('0') ? '0' : intPart);
  const trimmedDecimal = decimalPart.slice(0, decimals);
  if (decimals === 0) return String(Math.max(0, Number(safeInt || 0)));
  if (!trimmedDecimal && normalized.endsWith('.')) return `${safeInt}.`;
  return `${safeInt}${trimmedDecimal ? `.${trimmedDecimal}` : ''}`;
};

const normalizeRemitoSegment = (value, maxLength) => String(value || '').replace(/\D/g, '').slice(0, maxLength);

const splitRemitoNumber = value => {
  const digits = normalizeRemitoSegment(value, 12);
  if (!digits) return { puntoVenta: '', numero: '' };

  if (digits.length <= 4) {
    return { puntoVenta: digits, numero: '' };
  }

  return {
    puntoVenta: digits.slice(0, Math.min(4, digits.length)),
    numero: digits.slice(4, 12),
  };
};

const buildRemitoNumber = (puntoVenta, numero) => {
  const normalizedPuntoVenta = normalizeRemitoSegment(puntoVenta, 4);
  const normalizedNumero = normalizeRemitoSegment(numero, 8);
  if (!normalizedPuntoVenta && !normalizedNumero) return '';
  return `${normalizedPuntoVenta.padStart(4, '0')}${normalizedNumero.padStart(8, '0')}`;
};

const formatFormDate = date => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatFormTime = date => {
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
};

const buildFreshEmissionFormValues = ({ user, sucursalId }) => {
  const now = new Date();

  return {
    numero_guia: '',
    fecha_emision: formatFormDate(now),
    hora_emision: formatFormTime(now),
    usuario: user?.username || user?.name || 'usuario.demo',
    estado: 'EMITIDA',
    sucursal_origen_id: sucursalId ? Number(sucursalId) : '',
    sucursal_destino_id: '',
    sucursal_via_id: '',
    linea_numero: '',
    remitente_id: '',
    destinatario_id: '',
    cliente_pagador_id: '',
    subcuenta_id: '',
    domicilio_remitente: '',
    localidad_remitente: '',
    provincia_remitente: '',
    telefono_remitente: '',
    contacto_remitente: '',
    domicilio_destinatario: '',
    localidad_destinatario: '',
    provincia_destinatario: '',
    telefono_destinatario: '',
    contacto_destinatario: '',
    localidad_emision_id: '',
    localidad_destino_id: '',
    localidad_encargada_id: '',
    modalidad_cobro: 'cta_cte_destino',
    tipo_entrega: 'domicilio',
    contrareembolso: 0,
    total_valorizado: 0,
    valorizada: false,
    observaciones: '',
    remitos: [],
  };
};

const isTextEntryTarget = target => {
  if (!(target instanceof HTMLElement)) return false;

  return Boolean(target.closest('input, textarea, select, [contenteditable="true"]'));
};

const promptPostEmitAction = async ({ guiaLabel }) => {
  let selectedAction = null;
  let removeKeyboardHandler = () => {};

  await Swal.fire({
    title: `Guía emitida: #${guiaLabel}`,
    text: 'Como en el flujo legacy, ahora podés seguir emitiendo otra guía, consultar la emitida o pasar directo a impresión.',
    icon: 'success',
    showConfirmButton: false,
    showCloseButton: true,
    html: `
      <div style="display:grid;gap:12px;margin-top:8px">
        <button type="button" data-post-emit-action="continue" style="height:42px;border-radius:12px;border:1px solid rgba(16,185,129,0.25);background:rgb(var(--brand));color:white;font-weight:700;cursor:pointer;">
          Seguir emitiendo <kbd style="margin-left:8px;padding:2px 6px;border:1px solid currentColor;border-radius:4px;font-size:11px;font-weight:700">Enter</kbd>
        </button>
        <button type="button" data-post-emit-action="view" style="height:42px;border-radius:12px;border:1px solid rgba(148,163,184,0.45);background:transparent;color:inherit;font-weight:600;cursor:pointer;">
          Ver guía <kbd style="margin-left:8px;padding:2px 6px;border:1px solid currentColor;border-radius:4px;font-size:11px;font-weight:700">V</kbd>
        </button>
        <button type="button" data-post-emit-action="print" style="height:42px;border-radius:12px;border:1px solid rgba(148,163,184,0.45);background:transparent;color:inherit;font-weight:600;cursor:pointer;">
          Imprimir <kbd style="margin-left:8px;padding:2px 6px;border:1px solid currentColor;border-radius:4px;font-size:11px;font-weight:700">P</kbd>
        </button>
        <span style="color:#64748b;font-size:12px">Esc: cerrar</span>
      </div>
    `,
    didOpen: popup => {
      const selectAction = action => {
        selectedAction = action;
        Swal.close();
      };
      const actions = { Enter: 'continue', v: 'view', p: 'print' };
      const onKeyDown = event => {
        if (event.altKey || event.ctrlKey || event.metaKey || isTextEntryTarget(event.target)) return;

        if (event.key === 'Escape') {
          event.preventDefault();
          Swal.close();
          return;
        }

        const action = actions[event.key.length === 1 ? event.key.toLowerCase() : event.key];
        if (action) {
          event.preventDefault();
          selectAction(action);
        }
      };

      popup.querySelectorAll('[data-post-emit-action]').forEach(button => {
        button.addEventListener('click', () => selectAction(button.getAttribute('data-post-emit-action')));
      });
      window.addEventListener('keydown', onKeyDown, true);
      removeKeyboardHandler = () => window.removeEventListener('keydown', onKeyDown, true);
    },
    willClose: () => removeKeyboardHandler(),
  });

  return selectedAction;
};

const promptPrintModeAction = async () => {
  let selectedAction = null;
  let removeKeyboardHandler = () => {};

  await Swal.fire({
    title: 'Imprimir guía',
    text: 'Elegí si querés abrir el diálogo de impresión ahora o ver la versión PDF/previa del comprobante.',
    icon: 'question',
    showConfirmButton: false,
    showCloseButton: true,
    html: `
      <div style="display:grid;gap:12px;margin-top:8px">
        <button type="button" data-print-mode-action="direct" style="height:42px;border-radius:12px;border:1px solid rgba(16,185,129,0.25);background:rgb(var(--brand));color:white;font-weight:700;cursor:pointer;">
          Directo a impresora <kbd style="margin-left:8px;padding:2px 6px;border:1px solid currentColor;border-radius:4px;font-size:11px;font-weight:700">Enter</kbd>
        </button>
        <button type="button" data-print-mode-action="preview" style="height:42px;border-radius:12px;border:1px solid rgba(148,163,184,0.45);background:transparent;color:inherit;font-weight:600;cursor:pointer;">
          Ver PDF / previa <kbd style="margin-left:8px;padding:2px 6px;border:1px solid currentColor;border-radius:4px;font-size:11px;font-weight:700">V</kbd>
        </button>
        <button type="button" data-print-mode-action="cancel" style="height:42px;border-radius:12px;border:1px solid rgba(148,163,184,0.45);background:transparent;color:inherit;font-weight:600;cursor:pointer;">
          Cancelar <kbd style="margin-left:8px;padding:2px 6px;border:1px solid currentColor;border-radius:4px;font-size:11px;font-weight:700">Esc</kbd>
        </button>
      </div>
    `,
    didOpen: popup => {
      const selectAction = action => {
        selectedAction = action;
        Swal.close();
      };
      const actions = { Enter: 'direct', v: 'preview' };
      const onKeyDown = event => {
        if (event.altKey || event.ctrlKey || event.metaKey || isTextEntryTarget(event.target)) return;

        if (event.key === 'Escape') {
          event.preventDefault();
          selectAction('cancel');
          return;
        }

        const action = actions[event.key.length === 1 ? event.key.toLowerCase() : event.key];
        if (action) {
          event.preventDefault();
          selectAction(action);
        }
      };

      popup.querySelectorAll('[data-print-mode-action]').forEach(button => {
        button.addEventListener('click', () => selectAction(button.getAttribute('data-print-mode-action')));
      });
      window.addEventListener('keydown', onKeyDown, true);
      removeKeyboardHandler = () => window.removeEventListener('keydown', onKeyDown, true);
    },
    willClose: () => removeKeyboardHandler(),
  });

  return selectedAction;
};

const promptDirectPrintSetup = async ({ reason }) => {
  let selectedAction = null;
  const agentMissing = reason === 'connector_unavailable';
  const title = agentMissing
    ? 'Instalá Sinamon Print Agent para imprimir directamente'
    : 'Configurá una impresora para las guías';
  const description = agentMissing
    ? 'No detectamos Sinamon Print Agent conectado en esta computadora. Es el programa que conecta Sinamon con tus impresoras.'
    : 'Sinamon Print Agent está conectado, pero no encontramos una impresora disponible para imprimir guías.';
  const steps = agentMissing
    ? '<ol style="margin:12px 0 0;padding-left:20px;text-align:left"><li>Abrí la configuración de impresión: ahí descargás el agente y generás el código para vincular esta computadora.</li><li>Cuando la página muestre tus impresoras, elegí la de guías.</li><li>Volvé acá y elegí <strong>Reintentar impresión</strong>.</li></ol>'
    : '<ol style="margin:12px 0 0;padding-left:20px;text-align:left"><li>Verificá que la impresora esté instalada y encendida.</li><li>Abrí la configuración de impresión y elegí la impresora de guías.</li><li>Volvé acá y elegí <strong>Reintentar impresión</strong>.</li></ol>';

  await Swal.fire({
    title,
    icon: 'info',
    showConfirmButton: false,
    showCloseButton: true,
    html: `
      <p style="margin:0;text-align:left">${description}</p>
      ${steps}
      <div style="display:grid;gap:12px;margin-top:18px">
        <button type="button" data-direct-print-configure style="height:42px;border-radius:12px;border:0;background:rgb(var(--brand));color:white;font-weight:700;cursor:pointer;">Abrir configuración de impresión</button>
        <button type="button" data-direct-print-action="retry" style="height:42px;border-radius:12px;border:1px solid rgba(148,163,184,0.45);background:transparent;color:inherit;font-weight:600;cursor:pointer;">Reintentar impresión</button>
        <button type="button" data-direct-print-action="continue" style="height:42px;border-radius:12px;border:1px solid rgba(148,163,184,0.45);background:transparent;color:inherit;font-weight:600;cursor:pointer;">Seguir emitiendo</button>
      </div>
    `,
    didOpen: popup => {
      const selectAction = action => {
        selectedAction = action;
        Swal.close();
      };
      popup.querySelectorAll('[data-direct-print-action]').forEach(button => {
        button.addEventListener('click', () => selectAction(button.getAttribute('data-direct-print-action')));
      });
      popup.querySelector('[data-direct-print-configure]')?.addEventListener('click', () => {
        window.open(`${window.location.origin}/configuracion/impresion`, '_blank', 'noopener,noreferrer');
      });
    },
  });

  return selectedAction;
};

const normalizeEmittedGuide = response => {
  const id = toPositiveIntOrUndefined(
    response?.id
    ?? response?.id_guia
    ?? response?.guia_id,
  );

  return {
    id: id ? String(id) : null,
    numero: toNullableString(
      response?.numero
      ?? response?.numero_guia,
    ) || null,
    numeroGuia: toNullableString(response?.numero_guia) || null,
  };
};

export default function EmisionGuiaV3() {
  const client = useClient();
  const { user, tenant, token, sucursalActiva } = useAuth();
  const { layoutSettings } = useUI();
  const navigate = useNavigate();
  const location = useLocation();
  const emitirAttemptRef = useRef(0);
  const emissionIdempotencyKeyRef = useRef(null);
  const submitLockRef = useRef(false);
  const draftAutosaveDisabledRef = useRef(false);
  const draftAutosaveTimeoutRef = useRef(null);
  const addRemitoLockRef = useRef(false);
  const formRef = useRef(null);
  const confirmEmitButtonRef = useRef(null);
  const {
    form,
    remitosArray,
    addRemito,
    updateRemito,
    removeRemito,
    fetchingClientes,
    clientesRecords,
    totalBultos,
    totalKgReales,
    totalM3,
    valorDeclaradoTotal,
  } = useGuiaForm();
  const { register, watch, setValue, getValues, trigger, formState: { errors, isSubmitting } } = form;

  const [remitoDraft, setRemitoDraft] = useState(remitoDefault);
  const [retornables, setRetornables] = useState([]);
  const [retornableDraft, setRetornableDraft] = useState({ tipo_retornable_id: '', cantidad: '', propietario_tipo: 'CLIENTE' });
  const [editingRemitoDraft, setEditingRemitoDraft] = useState(remitoDefault);
  const [remitoErrors, setRemitoErrors] = useState({});
  const [liveWarnings, setLiveWarnings] = useState([]);
  const [liveErrors, setLiveErrors] = useState([]);
  const [isEmitiendo, setIsEmitiendo] = useState(false);
  const [highlightRemitoId, setHighlightRemitoId] = useState(null);
  const [activeRemitoId, setActiveRemitoId] = useState(null);
  const [modalValorizacionOpen, setModalValorizacionOpen] = useState(false);
  const [pendingPayload, setPendingPayload] = useState(null);
  const [tarifaPreview, setTarifaPreview] = useState(null);
  const [tarifaPreviewError, setTarifaPreviewError] = useState('');
  const [isCalculatingTariff, setIsCalculatingTariff] = useState(false);
  const [valuationMode, setValuationMode] = useState(VALUATION_MODE.AUTO);
  const [selectedTariffVersionId, setSelectedTariffVersionId] = useState('');
  const [showAlternativeTariffs, setShowAlternativeTariffs] = useState(false);
  const [manualValuationAmount, setManualValuationAmount] = useState('');
  const [manualValuationReason, setManualValuationReason] = useState('');
  const [routingPreview, setRoutingPreview] = useState(null);
  const [routingError, setRoutingError] = useState('');
  const [editingRowIndex, setEditingRowIndex] = useState(null);
  const [showRemitenteSnapshot, setShowRemitenteSnapshot] = useState(false);
  const [showDestinatarioSnapshot, setShowDestinatarioSnapshot] = useState(false);
  const [showClientePagador, setShowClientePagador] = useState(false);
  const [observacionesOpen, setObservacionesOpen] = useState(false);
  const [selectedClientesMap, setSelectedClientesMap] = useState({});
  const [subcuentasActivas, setSubcuentasActivas] = useState([]);
  const [fetchingSubcuentas, setFetchingSubcuentas] = useState(false);
  const [provinceOptions, setProvinceOptions] = useState([]);
  const [remitenteProvinciaId, setRemitenteProvinciaId] = useState('');
  const [destinatarioProvinciaId, setDestinatarioProvinciaId] = useState('');
  const [remitenteLocalidadId, setRemitenteLocalidadId] = useState('');
  const [destinatarioLocalidadId, setDestinatarioLocalidadId] = useState('');
  const [remitenteLocalidadOptions, setRemitenteLocalidadOptions] = useState([]);
  const [destinatarioLocalidadOptions, setDestinatarioLocalidadOptions] = useState([]);
  const [isCompactHeight, setIsCompactHeight] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }
    return window.innerHeight < 750;
  });

  const quickLineaRef = useRef(null);
  const remitenteRef = useRef(null);
  const destinatarioRef = useRef(null);
  const remitoPuntoVentaInputRef = useRef(null);
  const remitoNumeroInputRef = useRef(null);
  const destinatarioSelectionRef = useRef(0);

  const canEmitir = hasPermission(user, 'GUIA_EMITIR');
  const canOverrideTariff = hasPermission(user, 'TARIFAS_OVERRIDE_MANUAL');
  const canEmitWithoutValuation = hasPermission(user, 'GUIAS_EMITIR_SIN_VALORIZAR') || canOverrideTariff;
  const sucursalId = Number(sucursalActiva?.id || sucursalActiva?.sucursal_id || user?.sucursal_activa_id || user?.sucursal_id || user?.sucursal?.id || 0) || null;

  // Una guía originada en un retiro conserva el retiro como referencia externa y
  // propone sus datos; la línea y la valorización siguen siendo una decisión de emisión.
  useEffect(() => {
    const retiro = location.state?.retiroOrigen;
    if (!retiro?.id || retiro.estado !== 'RETIRADO') return;
    setValue('remitente_id', String(retiro.remitenteClienteId || ''), { shouldValidate: true });
    setValue('destinatario_id', String(retiro.destinatarioClienteId || ''), { shouldValidate: true });
    setValue('domicilio_remitente', retiro.remitenteDomicilio || '');
    setValue('telefono_remitente', retiro.remitenteTelefono || '');
    setValue('domicilio_destinatario', retiro.destinatarioDomicilio || '');
    setValue('telefono_destinatario', retiro.destinatarioTelefono || '');
    setValue('localidad_destino_id', String(retiro.destinatarioLocalidadId || ''), { shouldValidate: true });
    setValue('modalidad_cobro', retiro.formaPago || '', { shouldValidate: true });
    setValue('observaciones', [getValues('observaciones'), `Generada desde retiro ${retiro.nroRetiro}.`].filter(Boolean).join('\n'));
    setValue('retiro_id', String(retiro.id));
    if (!getValues('remitos')?.length) {
      addRemito({
        ...remitoDefault,
        numero_remito: retiro.remitoCliente || `RETIRO-${retiro.nroRetiro}`,
        bultos: Number(retiro.bultos || 0), kg_reales: Number(retiro.kilosReales || 0),
        m3: Number(retiro.mts3 || 0), kg_aforado: Number(retiro.kilosAforados || 0),
        valor_declarado: Number(retiro.valorDeclarado || 0), descripcion: retiro.descripcion || 'Mercadería retirada',
      });
    }
  }, [addRemito, getValues, location.state, setValue]);

  // Goods received with labels before the guía: the guía documents the same remitos and
  // the server links the existing labels (etiqueta_lote_id) instead of creating new ones.
  useEffect(() => {
    const prefill = mapEtiquetaOrigenToGuia(location.state?.etiquetaOrigen);
    if (!prefill) return;
    Object.entries(prefill.values).forEach(([field, value]) => {
      setValue(field, value, { shouldValidate: field.endsWith('_id') && field !== 'etiqueta_lote_id' });
    });
    setValue('observaciones', [getValues('observaciones'), prefill.observacion].filter(Boolean).join('\n'));
    if (!getValues('remitos')?.length) {
      prefill.remitos.forEach(remito => addRemito({ ...remitoDefault, ...remito }));
    }
  }, [addRemito, getValues, location.state, setValue]);

  useEffect(() => {
    if (!modalValorizacionOpen) {
      return undefined;
    }

    const focusTimer = window.setTimeout(() => {
      confirmEmitButtonRef.current?.focus?.();
    }, 0);

    return () => window.clearTimeout(focusTimer);
  }, [modalValorizacionOpen]);

  useEffect(() => {
    let cancelled = false;

    getProvinces()
      .then(data => {
        if (!cancelled) {
          setProvinceOptions(Array.isArray(data) ? data : []);
        }
      })
      .catch(error => console.warn('No se pudieron cargar las provincias para emisión de guía', error));

    return () => {
      cancelled = true;
    };
  }, []);

  const lineaId = watch('linea_numero');
  const modalidadCobro = watch('modalidad_cobro');
  const tipoEntrega = watch('tipo_entrega');
  const destinatarioId = watch('destinatario_id');
  const remitenteId = watch('remitente_id');
  const clientePagadorId = watch('cliente_pagador_id');
  const subcuentaId = watch('subcuenta_id');
  const contrareembolso = watch('contrareembolso');
  const fechaEmision = watch('fecha_emision');
  const localidadDestinoId = watch('localidad_destino_id');
  const remitenteProvinciaNombre = watch('provincia_remitente');
  const destinatarioProvinciaNombre = watch('provincia_destinatario');
  const remitenteLocalidadNombre = watch('localidad_remitente');
  const destinatarioLocalidadNombre = watch('localidad_destinatario');
  const compactModeEnabled = layoutSettings.compactMode || isCompactHeight;
  const draftSessionKey = useMemo(() => buildDraftSessionKey(user, sucursalId), [user, sucursalId]);

  const pauseDraftAutosave = useCallback(() => {
    draftAutosaveDisabledRef.current = true;
    if (draftAutosaveTimeoutRef.current) {
      clearTimeout(draftAutosaveTimeoutRef.current);
      draftAutosaveTimeoutRef.current = null;
    }
  }, []);

  const resetToFreshEmission = useCallback(() => {
    form.reset(buildFreshEmissionFormValues({ user, sucursalId }));
    setRemitoDraft(remitoDefault);
    setEditingRemitoDraft(remitoDefault);
    setEditingRowIndex(null);
    setRemitoErrors({});
    setLiveErrors([]);
    setLiveWarnings([]);
    setTarifaPreview(null);
    setTarifaPreviewError('');
    setIsCalculatingTariff(false);
    setValuationMode(VALUATION_MODE.AUTO);
    setSelectedTariffVersionId('');
    setShowAlternativeTariffs(false);
    setManualValuationAmount('');
    setManualValuationReason('');
    setRoutingPreview(null);
    setRoutingError('');
    setSelectedClientesMap({});
    setHighlightRemitoId(null);
    setActiveRemitoId(null);
    setShowRemitenteSnapshot(false);
    setShowDestinatarioSnapshot(false);
    setShowClientePagador(false);
    setObservacionesOpen(false);
    setRemitenteProvinciaId('');
    setDestinatarioProvinciaId('');
    setRemitenteLocalidadId('');
    setDestinatarioLocalidadId('');
    setRemitenteLocalidadOptions([]);
    setDestinatarioLocalidadOptions([]);
    setRetornables([]);
    setRetornableDraft({ tipo_retornable_id: '', cantidad: '', propietario_tipo: 'CLIENTE' });
    emissionIdempotencyKeyRef.current = null;
  }, [form, sucursalId, user]);

  const [{ data: lineasData }] = useQuery({
    query: LINEAS_QUERY,
    variables: { sucursalId: Number(sucursalId) },
    pause: !sucursalId,
  });

  const [{ data: retornablesTiposData }] = useQuery({
    query: RETORNABLES_TIPOS_QUERY,
    variables: { activo: true },
    pause: !hasPermission(user, 'RETORNABLES_VER'),
  });
  const retornablesTipos = retornablesTiposData?.retornablesTipos || [];

  const lineasOptions = useMemo(
    () => (lineasData?.lineas || []).map(linea => ({
      value: String(linea.id),
      label: formatLineaLabel(linea),
      raw: linea,
    })),
    [lineasData],
  );

  const selectedLinea = useMemo(
    () => lineasData?.lineas?.find(item => String(item.id) === String(lineaId)),
    [lineasData, lineaId],
  );

  const modalidadOptions = useMemo(() => [
    { value: 'cta_cte_destino', label: 'Cta cte destino' },
    { value: 'contado_origen', label: 'Contado origen' },
    { value: 'contado_destino', label: 'Contado destino' },
    { value: 'cta_cte_origen', label: 'Cta cte origen' },
  ], []);

  const entregaOptions = useMemo(() => [
    { value: 'domicilio', label: 'Envío a domicilio' },
    { value: 'retiro_sucursal', label: 'Retiro en sucursal' },
  ], []);

  const totals = useMemo(() => ({
    bultos: totalBultos,
    kgReales: totalKgReales,
    m3: totalM3,
    kgAforado: remitosArray.fields.reduce((acc, r) => acc + Number(r.kg_aforado || 0), 0),
    valorDeclarado: valorDeclaradoTotal,
    contrareembolso: Number(contrareembolso || 0),
    cantidad: remitosArray.fields.length,
  }), [totalBultos, totalKgReales, totalM3, valorDeclaradoTotal, remitosArray.fields, contrareembolso]);

  const rememberSelectedCliente = useCallback(cliente => {
    if (!cliente?.id) {
      return;
    }

    setSelectedClientesMap(prev => ({
      ...prev,
      [String(cliente.id)]: cliente,
    }));
  }, []);

  const clientesCatalog = useMemo(() => {
    const merged = new Map();

    clientesRecords.forEach(cliente => {
      if (cliente?.id !== undefined && cliente?.id !== null) {
        merged.set(String(cliente.id), cliente);
      }
    });

    Object.values(selectedClientesMap).forEach(cliente => {
      if (cliente?.id !== undefined && cliente?.id !== null) {
        merged.set(String(cliente.id), cliente);
      }
    });

    return Array.from(merged.values());
  }, [clientesRecords, selectedClientesMap]);

  const findClienteById = useCallback(
    id => {
      if (id === null || id === undefined || id === '') {
        return null;
      }

      return clientesCatalog.find(item => String(item.id) === String(id)) || null;
    },
    [clientesCatalog],
  );

  const remitenteNombre = findClienteById(remitenteId)?.razonSocial || 'Pendiente';
  const destinatarioNombre = findClienteById(destinatarioId)?.razonSocial || 'Pendiente';
  const pagadorNombre = findClienteById(clientePagadorId)?.razonSocial || 'Pendiente';
  const clienteValorizacionId = useMemo(
    () => resolveClienteValorizacionId({ clientePagadorId, modalidadCobro, remitenteId, destinatarioId }),
    [clientePagadorId, destinatarioId, modalidadCobro, remitenteId],
  );
  const valorizacionCongelada = Number(watch('total_valorizado') || 0);
  const detalleTarifaPreview = useMemo(() => parseJsonSafe(tarifaPreview?.desglose) || [], [tarifaPreview?.desglose]);
  const tarifaPreviewWarnings = useMemo(
    () => tarifaPreview?.simulation?.warnings || [],
    [tarifaPreview?.simulation?.warnings],
  );
  const tarifaVersionesCandidatas = useMemo(
    () => Array.isArray(tarifaPreview?.versiones_candidatas) ? tarifaPreview.versiones_candidatas : [],
    [tarifaPreview?.versiones_candidatas],
  );
  const selectedTariffVersion = useMemo(() => {
    if (!selectedTariffVersionId) return tarifaPreview?.version_principal || null;
    return tarifaVersionesCandidatas.find(item => String(item.id) === String(selectedTariffVersionId))
      || tarifaPreview?.version_principal
      || null;
  }, [selectedTariffVersionId, tarifaPreview?.version_principal, tarifaVersionesCandidatas]);
  const resolvedEmissionPayload = useMemo(() => {
    if (!pendingPayload) return null;
    const valuationSnapshot = buildValuationSnapshotForDecision({
      mode: valuationMode,
      pendingPayload,
      tarifaPreview,
      detalleTarifaPreview,
      tarifaPreviewWarnings,
      clienteValorizacionId,
      manualAmount: manualValuationAmount,
      manualReason: manualValuationReason,
      selectedVersion: selectedTariffVersion,
    });
    return {
      ...pendingPayload,
      ...valuationSnapshot,
    };
  }, [clienteValorizacionId, detalleTarifaPreview, manualValuationAmount, manualValuationReason, pendingPayload, selectedTariffVersion, tarifaPreview, tarifaPreviewWarnings, valuationMode]);
  const manualValuationIsValid = useMemo(() => {
    const amount = Number(manualValuationAmount || 0);
    return amount > 0 && toNullableString(manualValuationReason).length > 0;
  }, [manualValuationAmount, manualValuationReason]);
  const valuationDecisionError = useMemo(() => {
    if (!pendingPayload) return '';
    if (valuationMode === VALUATION_MODE.SIN_VALORIZAR) {
      return canEmitWithoutValuation ? '' : 'No tiene permisos para emitir sin valorizar. Requiere el permiso GUIAS_EMITIR_SIN_VALORIZAR o TARIFAS_OVERRIDE_MANUAL.';
    }
    if (valuationMode === VALUATION_MODE.MANUAL || valuationMode === VALUATION_MODE.OVERRIDE) {
      if (valuationMode === VALUATION_MODE.OVERRIDE && !canOverrideTariff) {
        return 'No tiene permisos para aplicar override manual tarifario. Requiere el permiso TARIFAS_OVERRIDE_MANUAL.';
      }
      return manualValuationIsValid ? '' : 'La carga manual/override exige importe y motivo.';
    }
    if (tarifaPreviewError) return tarifaPreviewError;
    if (isCalculatingTariff) return 'Espere a que termine el cálculo tarifario.';
    return '';
  }, [canEmitWithoutValuation, canOverrideTariff, isCalculatingTariff, manualValuationIsValid, pendingPayload, tarifaPreviewError, valuationMode]);
  const valuationStatus = useMemo(() => {
    if (valuationMode === VALUATION_MODE.SIN_VALORIZAR) {
      return {
        label: 'SIN VALORIZAR',
        className: 'border-[rgba(var(--warning),0.28)] bg-[rgba(var(--warning),0.14)] text-[rgb(var(--warning))]',
      };
    }
    if (valuationMode === VALUATION_MODE.MANUAL || valuationMode === VALUATION_MODE.OVERRIDE) {
      return {
        label: 'MANUAL',
        className: 'border-[rgba(var(--brand),0.22)] bg-[rgba(var(--brand),0.12)] text-[rgb(var(--fg))]',
      };
    }
    return {
      label: 'SUGERIDA',
      className: 'border-[rgba(34,197,94,0.24)] bg-[rgba(34,197,94,0.12)] text-[rgb(21,128,61)]',
    };
  }, [valuationMode]);
  const valuationSummaryItems = useMemo(() => ([
    { label: 'Cliente pagador', value: pendingPayload?.cliente_pagador_snapshot?.nombre || pagadorNombre || 'Pendiente', wide: true },
    { label: 'Remitente', value: pendingPayload?.remitente_snapshot?.nombre || remitenteNombre || 'Pendiente', wide: true },
    { label: 'Destinatario', value: pendingPayload?.destinatario_snapshot?.nombre || destinatarioNombre || 'Pendiente', wide: true },
    { label: 'Bultos', value: totals.bultos || 0 },
    { label: 'Kg', value: totals.kgReales || 0 },
    { label: 'M3', value: totals.m3 || 0 },
    { label: 'Valor declarado', value: formatCurrency(totals.valorDeclarado) },
    { label: 'CRR', value: formatCurrency(totals.contrareembolso) },
    { label: 'Destino operativo', value: routingPreview?.sucursal_destino_nombre || 'Pendiente' },
    { label: 'Vía', value: routingPreview?.sucursal_via_nombre || 'Directo' },
    { label: 'Modalidad', value: routingPreview?.modalidad_atencion || 'DIRECTA' },
  ]), [
    pendingPayload?.cliente_pagador_snapshot?.nombre,
    pendingPayload?.destinatario_snapshot?.nombre,
    pendingPayload?.remitente_snapshot?.nombre,
    pagadorNombre,
    remitenteNombre,
    destinatarioNombre,
    routingPreview?.modalidad_atencion,
    routingPreview?.sucursal_destino_nombre,
    routingPreview?.sucursal_via_nombre,
    totals.bultos,
    totals.contrareembolso,
    totals.kgReales,
    totals.m3,
    totals.valorDeclarado,
  ]);

  const loadPartyLocalities = useCallback(async (party, provinciaId) => {
    const setOptions = party === 'remitente' ? setRemitenteLocalidadOptions : setDestinatarioLocalidadOptions;
    if (!provinciaId) {
      setOptions([]);
      return [];
    }

    try {
      const data = await getLocalities(provinciaId);
      const nextOptions = Array.isArray(data) ? data : [];
      setOptions(nextOptions);
      return nextOptions;
    } catch (error) {
      console.warn(`No se pudieron cargar las localidades de ${party}`, error);
      setOptions([]);
      return [];
    }
  }, []);

  const applyPartyGeoSelection = useCallback(async (party, provinciaId, localidadId = '') => {
    const setProvinciaId = party === 'remitente' ? setRemitenteProvinciaId : setDestinatarioProvinciaId;
    const setLocalidadId = party === 'remitente' ? setRemitenteLocalidadId : setDestinatarioLocalidadId;
    const provinciaField = party === 'remitente' ? 'provincia_remitente' : 'provincia_destinatario';
    const localidadField = party === 'remitente' ? 'localidad_remitente' : 'localidad_destinatario';

    setProvinciaId(toNullableString(provinciaId));
    setLocalidadId('');

    const provincia = provinceOptions.find(option => String(option.id) === String(provinciaId));
    setValue(provinciaField, provincia?.nombre || '', { shouldValidate: false, shouldDirty: true });

    if (!provinciaId) {
      setValue(localidadField, '', { shouldValidate: false, shouldDirty: true });
      if (party === 'destinatario') {
        setValue('localidad_destino_id', '', { shouldValidate: true, shouldDirty: true });
      }
      await loadPartyLocalities(party, '');
      return;
    }

    const localities = await loadPartyLocalities(party, provinciaId);
    if (!localidadId) {
      setValue(localidadField, '', { shouldValidate: false, shouldDirty: true });
      if (party === 'destinatario') {
        setValue('localidad_destino_id', '', { shouldValidate: true, shouldDirty: true });
      }
      return;
    }

    const localidad = localities.find(option => String(option.id) === String(localidadId));
    setLocalidadId(toNullableString(localidadId));
    setValue(localidadField, localidad?.nombre || '', { shouldValidate: false, shouldDirty: true });
    if (party === 'destinatario') {
      setValue('localidad_destino_id', toNullableString(localidadId), { shouldValidate: true, shouldDirty: true });
    }
  }, [loadPartyLocalities, provinceOptions, setValue]);

  const applyPartyGeoFromCliente = useCallback(async (party, cliente) => {
    const provinciaId = resolveClienteProvinciaId(cliente);
    const localidadId = resolveClienteLocalidadId(cliente);
    await applyPartyGeoSelection(party, provinciaId, localidadId);
  }, [applyPartyGeoSelection]);

  const handlePartyLocalidadChange = useCallback((party, localidadId) => {
    const setLocalidadId = party === 'remitente' ? setRemitenteLocalidadId : setDestinatarioLocalidadId;
    const localidadField = party === 'remitente' ? 'localidad_remitente' : 'localidad_destinatario';
    const options = party === 'remitente' ? remitenteLocalidadOptions : destinatarioLocalidadOptions;
    const localidad = options.find(option => String(option.id) === String(localidadId));

    setLocalidadId(toNullableString(localidadId));
    setValue(localidadField, localidad?.nombre || '', { shouldValidate: false, shouldDirty: true });

    if (party === 'destinatario') {
      setValue('localidad_destino_id', toNullableString(localidadId), { shouldValidate: true, shouldDirty: true });
    }
  }, [destinatarioLocalidadOptions, remitenteLocalidadOptions, setValue]);

  const checklistItems = useMemo(() => ([
    {
      key: 'linea',
      label: 'Línea seleccionada',
      complete: Boolean(lineaId),
      hint: lineaId ? selectedLinea?.codigo || 'Completa' : 'Falta seleccionar línea',
    },
    {
      key: 'remitente',
      label: 'Remitente completo',
      complete: Boolean(remitenteId),
      hint: remitenteId ? remitenteNombre : 'Falta seleccionar remitente',
    },
    {
      key: 'destinatario',
      label: 'Destinatario completo',
      complete: Boolean(destinatarioId),
      hint: destinatarioId ? destinatarioNombre : 'Falta seleccionar destinatario',
    },
    {
      key: 'remitos',
      label: 'Remitos cargados',
      complete: remitosArray.fields.length > 0,
      hint: remitosArray.fields.length > 0 ? `${remitosArray.fields.length} cargado(s)` : 'Agregue al menos 1 remito',
    },
  ]), [destinatarioId, destinatarioNombre, lineaId, remitosArray.fields.length, remitenteId, remitenteNombre, selectedLinea?.codigo]);
  const pendingChecklistItems = useMemo(() => checklistItems.filter(item => !item.complete), [checklistItems]);
  const pendingChecklistLabel = useMemo(() => {
    if (pendingChecklistItems.length === 0) {
      return 'Listo para emitir';
    }

    return pendingChecklistItems
      .map(item => item.label.replace(' completo', '').replace(' seleccionada', ''))
      .join(' · ');
  }, [pendingChecklistItems]);

  const headerSummaryItems = useMemo(() => ([
    { key: 'linea', label: 'Línea', value: selectedLinea?.codigo || 'Pendiente', pending: !selectedLinea?.codigo },
    { key: 'emitir', label: 'Emitir', value: 'F5' },
  ]), [selectedLinea?.codigo]);
  const headerMetricItems = useMemo(() => ([
    { key: 'bultos', label: 'Bultos', value: totals.bultos },
    { key: 'remitos', label: 'Remitos', value: totals.cantidad },
    { key: 'valorizado', label: 'Valorizado', value: formatCurrency(valorizacionCongelada) },
  ]), [totals.bultos, totals.cantidad, valorizacionCongelada]);

  useEffect(() => {
    setValue('sucursal_origen_id', Number(sucursalId || 0), { shouldValidate: true });
    setValue('usuario', user?.username || user?.name || 'usuario.demo', { shouldValidate: false });
  }, [setValue, sucursalId, user]);

  useEffect(() => {
    if (!provinceOptions.length || remitenteProvinciaId || !remitenteProvinciaNombre) return;
    const provincia = findOptionByName(provinceOptions, remitenteProvinciaNombre);
    if (provincia) {
      setRemitenteProvinciaId(String(provincia.id));
    }
  }, [provinceOptions, remitenteProvinciaId, remitenteProvinciaNombre]);

  useEffect(() => {
    if (!provinceOptions.length || destinatarioProvinciaId || !destinatarioProvinciaNombre) return;
    const provincia = findOptionByName(provinceOptions, destinatarioProvinciaNombre);
    if (provincia) {
      setDestinatarioProvinciaId(String(provincia.id));
    }
  }, [provinceOptions, destinatarioProvinciaId, destinatarioProvinciaNombre]);

  useEffect(() => {
    let cancelled = false;
    if (!remitenteProvinciaId) {
      setRemitenteLocalidadOptions([]);
      return undefined;
    }

    loadPartyLocalities('remitente', remitenteProvinciaId).then(localities => {
      if (cancelled || remitenteLocalidadId || !remitenteLocalidadNombre) return;
      const localidad = findOptionByName(localities, remitenteLocalidadNombre);
      if (localidad) {
        setRemitenteLocalidadId(String(localidad.id));
      }
    });

    return () => {
      cancelled = true;
    };
  }, [loadPartyLocalities, remitenteLocalidadId, remitenteLocalidadNombre, remitenteProvinciaId]);

  useEffect(() => {
    let cancelled = false;
    if (!destinatarioProvinciaId) {
      setDestinatarioLocalidadOptions([]);
      return undefined;
    }

    loadPartyLocalities('destinatario', destinatarioProvinciaId).then(localities => {
      if (cancelled || destinatarioLocalidadId || !destinatarioLocalidadNombre) return;
      const localidad = findOptionByName(localities, destinatarioLocalidadNombre);
      if (localidad) {
        setDestinatarioLocalidadId(String(localidad.id));
        setValue('localidad_destino_id', String(localidad.id), { shouldValidate: true, shouldDirty: false });
      }
    });

    return () => {
      cancelled = true;
    };
  }, [destinatarioLocalidadId, destinatarioLocalidadNombre, destinatarioProvinciaId, loadPartyLocalities, setValue]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const syncCompactHeight = () => {
      setIsCompactHeight(window.innerHeight < 750);
    };

    syncCompactHeight();
    window.addEventListener('resize', syncCompactHeight);
    return () => window.removeEventListener('resize', syncCompactHeight);
  }, []);

  useEffect(() => {
    quickLineaRef.current?.focus?.();
  }, []);

  useEffect(() => {
    const autoPagadorId = resolveAutoPagadorId(modalidadCobro, remitenteId, destinatarioId);
    if (!autoPagadorId) return;
    if (String(clientePagadorId || '') === String(autoPagadorId)) return;
    setValue('cliente_pagador_id', autoPagadorId, { shouldValidate: false, shouldDirty: true });
  }, [clientePagadorId, destinatarioId, modalidadCobro, remitenteId, setValue]);

  useEffect(() => {
    let cancelled = false;
    const clienteId = toNullableString(clientePagadorId);
    setValue('subcuenta_id', '', { shouldValidate: false, shouldDirty: true });
    setSubcuentasActivas([]);
    if (!clienteId) return undefined;
    setFetchingSubcuentas(true);
    client.query(Q_CLIENTE_SUBCUENTAS_ACTIVAS, { clienteId }, { requestPolicy: 'network-only' }).toPromise()
      .then(result => {
        if (cancelled) return;
        if (result.error) throw result.error;
        const subcuentas = Array.isArray(result.data?.subcuentasCliente) ? result.data.subcuentasCliente.filter(item => item?.activo !== false) : [];
        setSubcuentasActivas(subcuentas);
        if (subcuentas.length === 1) setValue('subcuenta_id', String(subcuentas[0].id), { shouldValidate: true, shouldDirty: true });
      })
      .catch(() => { if (!cancelled) setSubcuentasActivas([]); })
      .finally(() => { if (!cancelled) setFetchingSubcuentas(false); });
    return () => { cancelled = true; };
  }, [client, clientePagadorId, setValue]);

  useEffect(() => {
    if (observacionesOpen) {
      const frame = requestAnimationFrame(() => document.getElementById('observaciones_modal')?.focus?.());
      return () => cancelAnimationFrame(frame);
    }
    return undefined;
  }, [observacionesOpen]);

  useEffect(() => {
    const warnings = [];
    if (Number(contrareembolso || 0) > 0 && modalidadCobro === 'cta_cte_origen') {
      warnings.push('La modalidad seleccionada puede no permitir contrareembolso. Revise antes de emitir.');
    }
    if (routingPreview?.warnings?.length) {
      warnings.push(...routingPreview.warnings);
    }
    if (routingPreview?.modalidad_atencion === 'REDESPACHO' && routingPreview?.empresa_redespacho_nombre) {
      warnings.push(`Destino parametrizado por redespacho con ${routingPreview.empresa_redespacho_nombre}.`);
    }
    if (tarifaPreviewWarnings.length) {
      warnings.push(...tarifaPreviewWarnings);
    }
    setLiveWarnings(warnings);
  }, [contrareembolso, modalidadCobro, routingPreview, tarifaPreviewWarnings]);

  useEffect(() => {
    let cancelled = false;

    const runRoutingPreview = async () => {
      if (!sucursalId || !localidadDestinoId) {
        setRoutingPreview(null);
        setRoutingError('');
        setValue('sucursal_destino_id', '', { shouldValidate: false });
        setValue('sucursal_via_id', '', { shouldValidate: false });
        return;
      }

      try {
        const data = await previewRoutingGuia({
          sucursal_origen_id: Number(sucursalId),
          localidad_destino_id: Number(localidadDestinoId),
        });
        if (cancelled) return;
        if (data?.resolved === false) {
          setRoutingPreview(null);
          setRoutingError(data.message || 'La localidad destino no tiene sucursal operativa configurada.');
          setValue('sucursal_destino_id', '', { shouldValidate: false });
          setValue('sucursal_via_id', '', { shouldValidate: false });
          return;
        }
        setRoutingPreview({
          ...data,
          localidad_destino_nombre: getValues('localidad_destinatario') || '',
        });
        setRoutingError('');
        setValue('sucursal_destino_id', String(data?.sucursal_destino_id || ''), { shouldValidate: false });
        setValue('sucursal_via_id', String(data?.sucursal_via_id || ''), { shouldValidate: false });
      } catch (error) {
        if (cancelled) return;
        const message = error?.response?.data?.message || 'La localidad destino no tiene sucursal operativa configurada.';
        setRoutingPreview(null);
        setRoutingError(message);
        setValue('sucursal_destino_id', '', { shouldValidate: false });
        setValue('sucursal_via_id', '', { shouldValidate: false });
      }
    };

    runRoutingPreview();
    return () => {
      cancelled = true;
    };
  }, [getValues, localidadDestinoId, setValue, sucursalId]);

  useEffect(() => {
    const baseErrors = [];
    const data = getValues();
    if (!data.linea_numero) baseErrors.push('Debe seleccionar una línea');
    if (!data.modalidad_cobro) baseErrors.push('Complete modalidad de cobro');
    if (!data.tipo_entrega) baseErrors.push('Complete tipo de entrega');
    if (!data.remitente_id) baseErrors.push('Debe seleccionar remitente');
    if (!data.destinatario_id) baseErrors.push('Debe seleccionar destinatario');
    if (subcuentasActivas.length > 1 && !data.subcuenta_id) baseErrors.push('Debe seleccionar una subcuenta del cliente pagador');
    if (!data.localidad_destino_id) baseErrors.push('El destinatario debe tener localidad destino');
    if (routingError) baseErrors.push(routingError);
    if (!data.remitos?.length) baseErrors.push('Agregue al menos 1 remito');
    setLiveErrors(baseErrors);
  }, [lineaId, modalidadCobro, tipoEntrega, remitenteId, destinatarioId, remitosArray.fields.length, getValues, routingError, localidadDestinoId]);

  useEffect(() => {
    const id = setTimeout(async () => {
      if (!sucursalId || !clienteValorizacionId || !routingPreview?.sucursal_destino_id) {
        setTarifaPreview(null);
        setTarifaPreviewError('');
        setIsCalculatingTariff(false);
        setValue('total_valorizado', 0, { shouldValidate: true, shouldDirty: true });
        setValue('valorizada', false, { shouldValidate: false, shouldDirty: true });
        return;
      }
      setIsCalculatingTariff(true);
      setTarifaPreviewError('');
      try {
        const result = await simulateTariff({
          tenant,
          token,
          body: buildTariffSimulationBody({
            fecha: fechaEmision || new Date().toISOString().slice(0, 10),
            clienteId: clienteValorizacionId,
            sucursalId,
            routingPreview,
            localidadDestinoId,
            totals,
            contrareembolso,
            versionId: selectedTariffVersionId ? Number(selectedTariffVersionId) : undefined,
          }),
        });
        const adapted = adaptTariffSimulation(result);
        setTarifaPreview(adapted);
        const importeSugerido = Number(adapted?.importe_sugerido || 0);
        setValue('total_valorizado', importeSugerido, { shouldValidate: true, shouldDirty: true });
        setValue('valorizada', importeSugerido > 0, { shouldValidate: false, shouldDirty: true });
      } catch (error) {
        setTarifaPreview(null);
        setTarifaPreviewError(error?.message || 'No se pudo calcular la valorización de la guía.');
        setValue('total_valorizado', 0, { shouldValidate: true, shouldDirty: true });
        setValue('valorizada', false, { shouldValidate: false, shouldDirty: true });
      } finally {
        setIsCalculatingTariff(false);
      }
    }, 300);
    return () => clearTimeout(id);
  }, [clienteValorizacionId, contrareembolso, fechaEmision, localidadDestinoId, routingPreview, selectedTariffVersionId, setValue, sucursalId, tenant, token, totals]);

  useEffect(() => {
    const payload = getValues();
    if (draftAutosaveDisabledRef.current) {
      draftAutosaveDisabledRef.current = false;
      return undefined;
    }

    draftAutosaveTimeoutRef.current = setTimeout(() => {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({
        ts: Date.now(),
        sessionKey: draftSessionKey,
        form: {
          ...payload,
          estado: 'EMITIDA',
        },
        remitoDraft,
      }));
    }, 350);
    return () => {
      if (draftAutosaveTimeoutRef.current) {
        clearTimeout(draftAutosaveTimeoutRef.current);
        draftAutosaveTimeoutRef.current = null;
      }
    };
  }, [draftSessionKey, lineaId, modalidadCobro, tipoEntrega, remitenteId, destinatarioId, contrareembolso, remitosArray.fields, remitoDraft, getValues]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!parsed?.ts || Date.now() - parsed.ts > DRAFT_TTL_MS) {
        clearGuiaDraftStorage();
        return;
      }
      if (!parsed.sessionKey || parsed.sessionKey !== draftSessionKey) {
        clearGuiaDraftStorage();
        return;
      }
      if (parsed.form) {
        form.reset({
          ...parsed.form,
          estado: 'EMITIDA',
        });
        setValue('sucursal_origen_id', Number(sucursalId || 0), { shouldValidate: true });
      }
      if (parsed.remitoDraft) {
        const draftNumero = toNullableString(parsed.remitoDraft.numero_remito);
        const draftPuntoVenta = toNullableString(parsed.remitoDraft.punto_venta);
        const draftParts = draftPuntoVenta ? { puntoVenta: draftPuntoVenta, numero: draftNumero } : splitRemitoNumber(draftNumero);
        setRemitoDraft({
          ...remitoDefault,
          ...parsed.remitoDraft,
          punto_venta: draftParts.puntoVenta,
          numero_remito: draftParts.numero,
        });
      }
    } catch {
      clearGuiaDraftStorage();
    }
  }, [draftSessionKey, form, setValue, user]);

  useEffect(() => {
    if (!clientesCatalog.length) return;

    const remitenteExiste = remitenteId ? clientesCatalog.some(item => String(item.id) === String(remitenteId)) : true;
    const destinatarioExiste = destinatarioId ? clientesCatalog.some(item => String(item.id) === String(destinatarioId)) : true;
    const pagadorExiste = clientePagadorId ? clientesCatalog.some(item => String(item.id) === String(clientePagadorId)) : true;

    if (!remitenteExiste) {
      setValue('remitente_id', '', { shouldValidate: true });
      setValue('domicilio_remitente', '', { shouldValidate: false });
      setValue('localidad_remitente', '', { shouldValidate: false });
      setValue('provincia_remitente', '', { shouldValidate: false });
      setValue('telefono_remitente', '', { shouldValidate: false });
      setValue('contacto_remitente', '', { shouldValidate: false });
      setRemitenteProvinciaId('');
      setRemitenteLocalidadId('');
      setRemitenteLocalidadOptions([]);
      clearGuiaDraftStorage();
    }

    if (!destinatarioExiste) {
      setValue('destinatario_id', '', { shouldValidate: true });
      setValue('domicilio_destinatario', '', { shouldValidate: false });
      setValue('localidad_destinatario', '', { shouldValidate: false });
      setValue('provincia_destinatario', '', { shouldValidate: false });
      setValue('telefono_destinatario', '', { shouldValidate: false });
      setValue('contacto_destinatario', '', { shouldValidate: false });
      setValue('localidad_destino_id', '', { shouldValidate: false });
      setValue('sucursal_destino_id', '', { shouldValidate: false });
      setValue('sucursal_via_id', '', { shouldValidate: false });
      setDestinatarioProvinciaId('');
      setDestinatarioLocalidadId('');
      setDestinatarioLocalidadOptions([]);
      clearGuiaDraftStorage();
    }
    if (!pagadorExiste) {
      setValue('cliente_pagador_id', '', { shouldValidate: false });
    }
  }, [clientePagadorId, clientesCatalog, destinatarioId, remitenteId, setValue]);

  const handleDraftChange = (field, value) => {
    if (remitoErrors[field]) {
      setRemitoErrors(prev => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
    if (numericFieldRules[field]) {
      const normalized = normalizeNumericInput(value, numericFieldRules[field].decimals);
      setRemitoDraft(prev => ({ ...prev, [field]: normalized }));
      return;
    }
    setRemitoDraft(prev => ({ ...prev, [field]: value }));
  };

  const handlePuntoVentaChange = value => {
    if (remitoErrors.numero_remito) {
      setRemitoErrors(prev => {
        const next = { ...prev };
        delete next.numero_remito;
        return next;
      });
    }
    const { advance } = applyPuntoVentaInput(value);
    setRemitoDraft(prev => {
      const next = applyPuntoVentaInput(value, prev.numero_remito);
      return { ...prev, punto_venta: next.punto_venta, numero_remito: next.numero_remito };
    });

    // Move focus right away: keys typed before a deferred focus would land in the full field.
    if (advance) remitoNumeroInputRef.current?.focus?.();
  };

  const handleEditingDraftChange = (field, value) => {
    if (numericFieldRules[field]) {
      const normalized = normalizeNumericInput(value, numericFieldRules[field].decimals);
      setEditingRemitoDraft(prev => ({ ...prev, [field]: normalized }));
      return;
    }

    setEditingRemitoDraft(prev => ({ ...prev, [field]: value }));
  };

  const handleNumeroRemitoChange = value => {
    if (remitoErrors.numero_remito) {
      setRemitoErrors(prev => {
        const next = { ...prev };
        delete next.numero_remito;
        return next;
      });
    }
    const { numero_remito: nextNumero, advance } = applyNumeroRemitoInput(value);
    setRemitoDraft(prev => ({
      ...prev,
      numero_remito: nextNumero,
    }));

    if (advance) document.getElementById('bultos')?.focus?.();
  };

  const handleRemitoDraftBlur = useCallback(() => {
    setRemitoDraft(prev => {
      return {
        ...prev,
        punto_venta: prev.punto_venta ? normalizeRemitoSegment(prev.punto_venta, 4).padStart(4, '0') : '',
        numero_remito: prev.numero_remito ? normalizeRemitoSegment(prev.numero_remito, 8).padStart(8, '0') : '',
      };
    });
  }, []);

  const handleRemitenteSelect = cliente => {
    if (!cliente) return;
    rememberSelectedCliente(cliente);
    setValue('remitente_id', String(cliente.id), { shouldValidate: true });
    applyClienteToRemitente(setValue, cliente);
    applyPartyGeoFromCliente('remitente', cliente);
    requestAnimationFrame(() => destinatarioRef.current?.querySelector('input,button,[role="combobox"]')?.focus?.());
  };

  const handleDestinatarioSelect = useCallback(async cliente => {
    if (!cliente) return;
    rememberSelectedCliente(cliente);
    setValue('destinatario_id', String(cliente.id), { shouldValidate: true });
    const selectionToken = ++destinatarioSelectionRef.current;
    let resolvedCliente = cliente;
    const localidadIdInicial = resolveClienteLocalidadId(cliente);

    applyClienteToDestinatario(setValue, cliente);
    applyPartyGeoFromCliente('destinatario', cliente);

    if (!localidadIdInicial && cliente?.id) {
      try {
        const result = await client.query(Q_CLIENTE, { id: cliente.id }, { requestPolicy: 'network-only' }).toPromise();
        if (selectionToken !== destinatarioSelectionRef.current) return;
        if (result.error) throw result.error;
        resolvedCliente = result.data?.cliente || cliente;
      } catch (error) {
        console.warn('No se pudo cargar el detalle del destinatario para resolver la localidad', error);
      }
    }

    rememberSelectedCliente(resolvedCliente);
    applyClienteToDestinatario(setValue, resolvedCliente);
    applyPartyGeoFromCliente('destinatario', resolvedCliente);
    requestAnimationFrame(() => remitoPuntoVentaInputRef.current?.focus?.());
  }, [applyPartyGeoFromCliente, client, rememberSelectedCliente, setValue]);

  const handlePagadorSelect = useCallback(cliente => {
    if (!cliente) return;
    rememberSelectedCliente(cliente);
    setValue('cliente_pagador_id', String(cliente.id), { shouldValidate: false, shouldDirty: true });
    setValue('subcuenta_id', '', { shouldValidate: false, shouldDirty: true });
  }, [rememberSelectedCliente, setValue]);

  const handleAddRemito = useCallback(() => {
    if (addRemitoLockRef.current) {
      return;
    }
    addRemitoLockRef.current = true;

    const payload = {
      ...remitoDraft,
      numero_remito: buildRemitoNumber(remitoDraft.punto_venta, remitoDraft.numero_remito),
    };
    const result = addRemito(payload);
    if (!result.ok) {
      setRemitoErrors(result.errors || {});
      addRemitoLockRef.current = false;
      if (result.errors?.numero_remito) remitoNumeroInputRef.current?.focus();
      return;
    }
    setRemitoErrors({});
    const current = form.getValues('remitos');
    const nuevo = current[current.length - 1];
    if (nuevo?.id) {
      setActiveRemitoId(nuevo.id);
      setHighlightRemitoId(nuevo.id);
      setTimeout(() => setHighlightRemitoId(null), 800);
    }
    setRemitoDraft(remitoDefault);
    remitoNumeroInputRef.current?.focus();
    setTimeout(() => {
      addRemitoLockRef.current = false;
    }, 0);
  }, [addRemito, remitoDraft, form]);

  const startEditingRemito = useCallback((index, remito) => {
    setEditingRowIndex(index);
    setActiveRemitoId(remito?.id || null);
    setEditingRemitoDraft({
      numero_remito: splitRemitoNumber(remito?.numero_remito).numero || remito?.numero_remito || '',
      bultos: String(remito?.bultos ?? ''),
      kg_reales: String(remito?.kg_reales ?? ''),
      m3: String(remito?.m3 ?? ''),
      kg_aforado: String(remito?.kg_aforado ?? ''),
      valor_declarado: String(remito?.valor_declarado ?? ''),
      descripcion: remito?.descripcion || '',
    });
    setRemitoErrors({});
  }, []);

  const cancelEditingRemito = useCallback(() => {
    setEditingRowIndex(null);
    setEditingRemitoDraft(remitoDefault);
    setRemitoErrors({});
  }, []);

  const saveEditingRemito = useCallback(() => {
    if (editingRowIndex === null) {
      return true;
    }

    const currentRemito = remitosArray.fields[editingRowIndex];
    if (!currentRemito) {
      cancelEditingRemito();
      return true;
    }

    const currentParts = splitRemitoNumber(currentRemito.numero_remito);
    const payload = {
      ...currentRemito,
      ...editingRemitoDraft,
      numero_remito: buildRemitoNumber(currentParts.puntoVenta, editingRemitoDraft.numero_remito),
    };

    const result = updateRemito(editingRowIndex, payload);
    if (!result.ok) {
      setRemitoErrors(result.errors || {});
      return false;
    }

    cancelEditingRemito();
    return true;
  }, [cancelEditingRemito, editingRemitoDraft, editingRowIndex, remitosArray.fields, updateRemito]);

  const handleEnterNavigation = useCallback((event) => {
    if (event.key !== 'Enter' || event.altKey || event.ctrlKey || event.metaKey) {
      return;
    }

    const targetTag = event.target?.tagName;
    if (targetTag === 'TEXTAREA' || targetTag === 'BUTTON') {
      return;
    }

    // El combobox confirma su resultado con Enter; no adelantar el foco al botón Buscar.
    if (event.target?.closest?.('.cliente-combobox')) {
      return;
    }

    const root = event.currentTarget || formRef.current;
    if (!root?.querySelectorAll) {
      return;
    }

    const focusable = Array.from(
      root.querySelectorAll('input, select, textarea, button, [role="combobox"], [tabindex]:not([tabindex="-1"])'),
    ).filter(node => {
      if (node.disabled) return false;
      if (node.getAttribute('aria-hidden') === 'true') return false;
      return node.offsetParent !== null;
    });

    const currentIndex = focusable.indexOf(event.target);
    if (currentIndex === -1) {
      return;
    }

    const targetId = event.target?.id || '';
    const isRemitoEntryField = Boolean(event.target?.closest?.('[data-remito-entry="true"]'));
    const shouldSubmitRemito =
      !event.shiftKey
      && isRemitoEntryField
      && targetId === 'remito_descripcion';

    event.preventDefault();
    event.stopPropagation();

    if (shouldSubmitRemito) {
      handleAddRemito();
      return;
    }

    const direction = event.shiftKey ? -1 : 1;
    const nextIndex = currentIndex + direction;

    if (nextIndex >= 0 && nextIndex < focusable.length) {
      focusable[nextIndex]?.focus?.();
      return;
    }

    if (!event.shiftKey) {
      handleAddRemito();
    }
  }, [handleAddRemito]);

  const validarFormulario = useCallback(async () => {
    const ok = await trigger();
    if (!ok) {
      const schemaErrors = Object.values(errors).map(item => item?.message).filter(Boolean);
      return Array.from(new Set([...liveErrors, ...schemaErrors]));
    }
    const data = getValues();
    const baseErrors = [];
    if (!data.linea_numero) baseErrors.push('Debe seleccionar una línea');
    if (!data.modalidad_cobro) baseErrors.push('Complete modalidad de cobro');
    if (!data.tipo_entrega) baseErrors.push('Complete tipo de entrega');
    if (!data.remitente_id) baseErrors.push('Debe seleccionar remitente');
    if (!data.destinatario_id) baseErrors.push('Debe seleccionar destinatario');
    if (!data.localidad_destino_id) baseErrors.push('El destinatario debe tener localidad destino');
    if (routingError) baseErrors.push(routingError);
    if (!data.remitos?.length) baseErrors.push('Agregue al menos 1 remito');
    return baseErrors;
  }, [errors, getValues, liveErrors, trigger, routingError, subcuentasActivas.length]);

  const emitir = useCallback(async payload => {
    if (isEmitiendo) {
      return;
    }
    setIsEmitiendo(true);
    try {
      const response = await emitirGuia(payload);
      if (!response?.ok) {
        throw new Error(response?.error || response?.message || 'No se pudo emitir la guía');
      }
      pauseDraftAutosave();
      clearGuiaDraftStorage();
      const guiaEmitida = normalizeEmittedGuide(response);

      const continueEmitting = () => {
        pauseDraftAutosave();
        clearGuiaDraftStorage();
        resetToFreshEmission();
        requestAnimationFrame(() => quickLineaRef.current?.focus?.());
      };

      const printingPreferences = getLocalPrintingPreferences({ user, tenant });
      if (printingPreferences.autoPrintGuides) {
        continueEmitting();
        if (!guiaEmitida.id) {
          void Swal.fire({
            toast: true,
            position: 'top-end',
            title: 'La guía se emitió, pero no se recibió su identificador para imprimirla.',
            icon: 'warning',
            timer: 5000,
            showConfirmButton: false,
          });
          return;
        }

        void printGuiaDirectly(guiaEmitida.id, { user, tenant })
          .then((result) => {
            if (result.status === 'printed') return;
            void Swal.fire({
              toast: true,
              position: 'top-end',
              title: 'La guía se emitió, pero la impresión automática necesita configuración.',
              text: 'Revisá Sinamon Print Agent y la impresora de guías en Configuración > Impresión.',
              icon: 'warning',
              timer: 6500,
              showConfirmButton: false,
            });
          })
          .catch((error) => {
            void Swal.fire({
              toast: true,
              position: 'top-end',
              title: 'La guía se emitió, pero no se pudo imprimir automáticamente.',
              text: error?.message || 'Revisá la configuración de impresión.',
              icon: 'warning',
              timer: 6500,
              showConfirmButton: false,
            });
          });
        return;
      }

      if (!guiaEmitida.id) {
        await Swal.fire({
          title: `Guía emitida: #${guiaEmitida.numeroGuia || guiaEmitida.numero || 'N/D'}`,
          text: 'La guía se emitió correctamente.',
          icon: 'success',
          confirmButtonColor: 'rgb(var(--brand))',
        });
        navigate('/guias');
        return;
      }

      const postEmitAction = await promptPostEmitAction({
        guiaLabel: guiaEmitida.numeroGuia || guiaEmitida.numero || guiaEmitida.id,
      });

      if (postEmitAction === 'continue') {
        continueEmitting();
        return;
      }

      if (postEmitAction === 'print') {
        const printAction = await promptPrintModeAction();

        if (printAction === 'direct') {
          for (;;) {
            try {
              void Swal.fire({
                title: 'Verificando impresión directa',
                text: 'Estamos conectando esta computadora con la impresora. Puede demorar unos segundos la primera vez.',
                icon: 'info',
                showConfirmButton: false,
                allowEscapeKey: false,
                allowOutsideClick: false,
                didOpen: () => Swal.showLoading(),
              });
              const result = await printGuiaDirectly(guiaEmitida.id, { user, tenant });
              Swal.close();
              if (result.status === 'printed') {
                await Swal.fire({
                  title: 'Impresión enviada',
                  text: `La guía fue enviada a ${result.printerName}. Ya podés emitir la siguiente.`,
                  icon: 'success',
                  confirmButtonText: 'Seguir emitiendo',
                  confirmButtonColor: 'rgb(var(--brand))',
                });
                continueEmitting();
                return;
              }

              const action = await promptDirectPrintSetup({ reason: result.reason });
              if (action === 'retry') continue;
              if (action === 'continue') continueEmitting();
              return;
            } catch (error) {
              Swal.close();
              const action = await Swal.fire({
                title: 'No pudimos enviar la impresión directa',
                text: `${error?.message || 'Ocurrió un error inesperado.'} Podés reintentar o seguir emitiendo sin perder el ritmo de trabajo.`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Reintentar impresión',
                cancelButtonText: 'Seguir emitiendo',
                confirmButtonColor: 'rgb(var(--brand))',
              });
              if (action.isConfirmed) continue;
              continueEmitting();
              return;
            }
          }
        }

        if (printAction === 'preview') {
          navigate(`/guias/${guiaEmitida.id}`, {
            state: {
              activeTab: 'documentos',
              guiaId: guiaEmitida.id,
              guiaNumero: guiaEmitida.numeroGuia,
            },
          });
          return;
        }

        navigate(`/guias/${guiaEmitida.id}`, {
          state: {
            guiaId: guiaEmitida.id,
            guiaNumero: guiaEmitida.numeroGuia,
          },
        });
        return;
      }

      navigate(`/guias/${guiaEmitida.id}`, {
        state: {
          guiaId: guiaEmitida.id,
          guiaNumero: guiaEmitida.numeroGuia,
        },
      });
    } catch (error) {
      const detail = (error?.message && String(error.message).trim()) || (error?.status ? `HTTP ${error.status}` : 'Error desconocido.');
      const issueLines = Array.isArray(error?.payload?.validationIssues)
        ? error.payload.validationIssues.map(issue => `${issue.path}: ${issue.message}`)
        : [];
      const html = issueLines.length
        ? `<p style="text-align:left;margin:0 0 8px 0;"><strong>Campos con error:</strong></p><ul style="text-align:left">${issueLines.map(line => `<li>${line}</li>`).join('')}</ul>`
        : `<p style="text-align:left">${detail}</p>`;
      await Swal.fire({ title: 'Error al emitir guía', html, icon: 'error' });
    } finally {
      setIsEmitiendo(false);
      setPendingPayload(null);
      setModalValorizacionOpen(false);
    }
  }, [isEmitiendo, navigate, pauseDraftAutosave, resetToFreshEmission, tenant, user]);

  const handleEmitirGuia = useCallback(async (trigger = 'unknown') => {
    emitirAttemptRef.current += 1;
    if (isSubmitting || isEmitiendo || submitLockRef.current) {
      return;
    }
    submitLockRef.current = true;

    try {
      const validationErrors = await validarFormulario();
    if (validationErrors.length > 0) {
      setLiveErrors(validationErrors);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      await Swal.fire({
        title: 'Revise el formulario',
        html: `<ul style="text-align:left">${validationErrors.map(message => `<li>${message}</li>`).join('')}</ul>`,
        icon: 'warning',
      });
      return;
    }

    const raw = getValues();
    const lineaSeleccionada = resolveLineaSeleccionada(lineasData?.lineas, selectedLinea || raw.linea_numero || raw.linea_id);
    const payload = normalizePayloadForSubmit(raw, user, sucursalActiva, clientesCatalog, lineaSeleccionada);
    payload.retornables = retornables;
    emissionIdempotencyKeyRef.current ||= globalThis.crypto?.randomUUID?.() || `guia-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    payload.idempotency_key = emissionIdempotencyKeyRef.current;
    payload.total_valorizado = Number(tarifaPreview?.importe_sugerido || payload.total_valorizado || 0);
    payload.valorizada = payload.total_valorizado > 0;

    const missingSnapshotNames = [];
    if (!toNullableString(payload?.remitente_snapshot?.nombre)) missingSnapshotNames.push('remitente_snapshot.nombre');
    if (!toNullableString(payload?.destinatario_snapshot?.nombre)) missingSnapshotNames.push('destinatario_snapshot.nombre');
    if (missingSnapshotNames.length > 0) {
      const message = `No se puede emitir la guía: faltan ${missingSnapshotNames.join(', ')}.`;
      await Swal.fire({
        title: 'Faltan datos obligatorios',
        text: `${message} Verifique remitente y destinatario seleccionados.`,
        icon: 'warning',
      });
      return;
    }

    const parsedPayload = submitPayloadSchema.safeParse(payload);

    if (!parsedPayload.success) {
      const issues = parsedPayload.error.issues.map(issue => ({
        path: formatIssuePath(issue.path),
        message: issue.message,
        expected: issue.expected,
        received: issue.received,
      }));
      await Swal.fire({
        title: 'Error de validación',
        html: `<ul style="text-align:left">${issues.map(issue => `<li><strong>${issue.path}</strong>: ${issue.message}</li>`).join('')}</ul>`,
        icon: 'error',
      });
      return;
    }

    if (!payload.sucursal_id && !payload.sucursal_origen_id) {
      await Swal.fire({
        title: 'Falta sucursal de origen',
        text: 'No se pudo determinar sucursal_id/sucursal_origen_id para emitir la guía. Recargue la página e intente nuevamente.',
        icon: 'error',
      });
      return;
    }

    if (!routingPreview?.sucursal_destino_id) {
      await Swal.fire({
        title: 'Routing incompleto',
        text: 'La guía no puede emitirse sin una sucursal destino operativa resuelta automáticamente.',
        icon: 'warning',
      });
      return;
    }

    if (isCalculatingTariff) {
      await Swal.fire({
        title: 'Valorización en proceso',
        text: 'Espere a que termine el cálculo tarifario antes de emitir.',
        icon: 'info',
      });
      return;
    }

    if (tarifaPreviewError && !canEmitWithoutValuation) {
      await Swal.fire({
        title: 'No se pudo valorizar',
        text: tarifaPreviewError,
        icon: 'warning',
      });
      return;
    }

    const parsedPayloadWithFreeze = {
      ...parsedPayload.data,
      total_valorizado: Number(tarifaPreview?.importe_sugerido || parsedPayload.data.total_valorizado || 0),
      valorizada: Number(tarifaPreview?.importe_sugerido || parsedPayload.data.total_valorizado || 0) > 0,
    };

    setPendingPayload(parsedPayloadWithFreeze);
    setValuationMode(VALUATION_MODE.AUTO);
    setSelectedTariffVersionId(String(tarifaPreview?.version_principal?.id || ''));
    setShowAlternativeTariffs(false);
    setManualValuationAmount('');
    setManualValuationReason('');
    setModalValorizacionOpen(true);
    } finally {
    submitLockRef.current = false;
  }
  }, [canEmitWithoutValuation, clienteValorizacionId, clientesCatalog, getValues, isCalculatingTariff, isEmitiendo, isSubmitting, lineasData, retornables, routingPreview?.sucursal_destino_id, selectedLinea, sucursalActiva, tarifaPreview, tarifaPreviewError, user, validarFormulario]);

  const handleOcr = async () => {
    await Swal.fire({
      title: 'Leer remito (OCR)',
      text: 'TODO: Integrar proveedor OCR real para completar campos automáticamente.',
      icon: 'info',
    });
  };

  const handleCancelarEmision = useCallback(() => {
    pauseDraftAutosave();
    clearGuiaDraftStorage();
    navigate('/guias');
  }, [navigate, pauseDraftAutosave]);

  const handleSaveDraft = useCallback(() => {
    const payload = getValues();
    localStorage.setItem(DRAFT_KEY, JSON.stringify({
      ts: Date.now(),
      sessionKey: draftSessionKey,
      form: {
        ...payload,
        estado: 'EMITIDA',
      },
      remitoDraft,
    }));
    console.log('OperatorMode action', 'guardar_borrador', { remitos: payload?.remitos?.length || 0 });
    Swal.fire({ title: 'Borrador guardado', icon: 'success', timer: 1400, showConfirmButton: false });
  }, [draftSessionKey, getValues, remitoDraft]);

  const handleClearForm = useCallback(async () => {
    const confirm = await Swal.fire({
      title: '¿Limpiar formulario?',
      text: 'Se perderán los datos no emitidos.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: 'rgb(var(--brand))',
    });
    if (!confirm.isConfirmed) return;
    pauseDraftAutosave();
    resetToFreshEmission();
    clearGuiaDraftStorage();
  }, [pauseDraftAutosave, resetToFreshEmission]);

  const handleValorizacionModalShortcut = useCallback((event) => {
    const targetTag = event.target?.tagName?.toLowerCase();
    if (event.key === 'Enter' && targetTag === 'textarea') {
      return;
    }

    if (event.key === 'F2') {
      event.preventDefault();
      setModalValorizacionOpen(false);
      return;
    }

    if (event.key !== 'Enter' && event.key !== 'F5') {
      return;
    }

    event.preventDefault();

    if (!isEmitiendo && !valuationDecisionError && resolvedEmissionPayload) {
      emitir(resolvedEmissionPayload);
    }
  }, [emitir, isEmitiendo, resolvedEmissionPayload, valuationDecisionError]);

  useEffect(() => {
    if (!modalValorizacionOpen) {
      return undefined;
    }

    window.addEventListener('keydown', handleValorizacionModalShortcut, true);
    return () => window.removeEventListener('keydown', handleValorizacionModalShortcut, true);
  }, [handleValorizacionModalShortcut, modalValorizacionOpen]);

  const operatorMode = useOperatorMode({
    lineas: lineasData?.lineas || [],
    clientes: clientesRecords || [],
    state: {
      lineaId,
      remitenteId,
      destinatarioId,
      remitosCount: remitosArray.fields.length,
    },
    actions: {
      emitir: async () => {
        const validationErrors = await validarFormulario();
        if (validationErrors.length > 0) {
          setLiveErrors(validationErrors);
          return { ok: false, errors: validationErrors };
        }
        setLiveErrors([]);
        await handleEmitirGuia('operatorMode.emitir');
        return { ok: true };
      },
      valorizacion: () => setModalValorizacionOpen(true),
      ocr: handleOcr,
      guardarBorrador: handleSaveDraft,
      limpiar: handleClearForm,
      seleccionarLinea: linea => {
        console.log('seleccionarLinea', linea);
        setValue('linea_numero', String(linea.id), { shouldValidate: true });
      },
      seleccionarRemitente: cliente => {
        setValue('remitente_id', String(cliente.id), { shouldValidate: true });
        handleRemitenteSelect(cliente);
      },
      seleccionarDestinatario: cliente => {
        setValue('destinatario_id', String(cliente.id), { shouldValidate: true });
        handleDestinatarioSelect(cliente);
      },
      agregarRemitoNumero: numero => {
        const result = addRemito({
          ...remitoDefault,
          numero_remito: numero,
          bultos: 1,
          kg_reales: 0,
          m3: 0,
          kg_aforado: 0,
          valor_declarado: 0,
          descripcion: 'Remito agregado desde Modo Operador',
        });
        if (!result.ok) {
          return { ok: false, error: result.errors?.numero_remito || 'No se pudo agregar remito' };
        }
        return { ok: true };
      },
    },
  });
  const handleOperatorKeyDown = operatorMode.handleKeyDown;

  const handleGlobalKeys = useCallback((e) => {
    const paletteHandled = handleOperatorKeyDown(e, { allowEmitShortcut: true });
    if (paletteHandled) return;

    if (e.key === 'F2') {
      e.preventDefault();
      remitenteRef.current?.querySelector('input,button')?.focus?.();
      return;
    }
    if (e.key === 'F3') {
      e.preventDefault();
      destinatarioRef.current?.querySelector('input,button')?.focus?.();
      return;
    }
    if (e.key === 'F5') {
      e.preventDefault();
      handleEmitirGuia('hotkey:F5');
      return;
    }
    if (e.key === 'F7' || ((e.ctrlKey || e.metaKey) && String(e.key).toLowerCase() === 'o')) {
      e.preventDefault();
      setObservacionesOpen(true);
      return;
    }
    if (e.key === 'F8') {
      e.preventDefault();
      setShowClientePagador(prev => !prev);
      return;
    }
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      handleEmitirGuia('hotkey:Ctrl+Enter');
      return;
    }
    if (e.key === 'Escape') {
      if (editingRowIndex !== null) {
        e.preventDefault();
        cancelEditingRemito();
        return;
      }
      setObservacionesOpen(false);
      setModalValorizacionOpen(false);
      setPendingPayload(null);
      return;
    }
    if (e.key === 'Enter' && editingRowIndex !== null) {
      const isInsideTable = Boolean(document.activeElement?.closest?.('tbody'));
      if (isInsideTable) {
        e.preventDefault();
        saveEditingRemito();
        return;
      }
    }
  }, [cancelEditingRemito, editingRowIndex, handleEmitirGuia, handleOperatorKeyDown, saveEditingRemito]);

  useEffect(() => {
    window.addEventListener('keydown', handleGlobalKeys);
    return () => window.removeEventListener('keydown', handleGlobalKeys);
  }, [handleGlobalKeys]);


  return (
    <div className={`mx-auto w-full max-w-[1400px] px-2.5 pb-20 ${compactModeEnabled ? 'compact-mode' : ''}`}>
      <div className={`flex min-h-0 flex-col gap-1.5 overflow-hidden ${compactModeEnabled ? 'h-[calc(100vh-82px)]' : 'h-[calc(100vh-94px)]'}`}>
      <section className={`sticky top-2 z-20 rounded-2xl border border-[rgba(var(--border),0.65)] bg-[linear-gradient(180deg,rgba(var(--panel),0.985),rgba(var(--subpanel),0.88))] shadow-[0_10px_22px_rgba(var(--shadow),0.1)] backdrop-blur ${compactModeEnabled ? 'px-2 py-1.5' : 'px-3 py-2'}`}>
        <div className={`flex flex-wrap items-start justify-between ${compactModeEnabled ? 'gap-1.5' : 'gap-2'}`}>
          <div className="flex min-w-0 flex-wrap items-center gap-1.5">
            <h1 className={`mr-1 font-semibold leading-none text-[rgb(var(--fg))] ${compactModeEnabled ? 'text-[13px]' : 'text-sm'}`}>Emisión de Guía</h1>
            <button
              type="button"
              onClick={operatorMode.openPalette}
              className={`inline-flex items-center rounded-lg border border-[rgba(var(--border),0.68)] bg-[rgba(var(--subpanel),0.72)] font-medium text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))] ${compactModeEnabled ? 'h-6 px-1.5 text-[10px]' : 'h-7 px-2 text-[11px]'}`}
            >
              Alt+F2 Operador
            </button>
            {headerSummaryItems.map(item => (
              <span key={item.key} className={`inline-flex min-w-0 items-center gap-1 rounded-full border border-[rgba(var(--border),0.55)] bg-[rgba(var(--panel),0.88)] font-medium text-[rgb(var(--fg))] ${compactModeEnabled ? 'px-1.5 py-0 text-[10px]' : 'px-2 py-0.5'}`}>
                <span className={item.pending ? 'text-red-600' : 'text-[rgba(var(--muted),0.95)]'}>{item.label}</span>
                <span className={`max-w-[120px] truncate ${item.pending ? 'text-red-600' : ''}`}>{item.value}</span>
              </span>
            ))}
            <span className={`inline-flex min-w-0 items-center rounded-full font-medium ${compactModeEnabled ? 'px-1.5 py-0 text-[10px]' : 'px-2 py-0.5'} ${pendingChecklistItems.length === 0 ? 'border border-[rgba(var(--brand),0.24)] bg-[rgba(var(--brand),0.08)] text-[rgb(var(--brand))]' : 'border border-[rgba(var(--border),0.55)] bg-[rgba(var(--subpanel),0.52)] text-[rgb(var(--fg))]'}`}>
              {pendingChecklistLabel}
            </span>
          </div>
          <div className={`flex flex-wrap items-center justify-end ${compactModeEnabled ? 'gap-1 text-[10px]' : 'gap-1.5 text-[11px]'}`}>
            {headerMetricItems.map(item => (
              <span key={item.key} className={`inline-flex items-center gap-1 rounded-full border border-[rgba(var(--border),0.5)] bg-[rgba(var(--panel),0.92)] font-semibold text-[rgb(var(--fg))] ${compactModeEnabled ? 'px-1.5 py-0.5' : 'px-2 py-1'}`}>
                <span className="text-[rgba(var(--muted),0.9)]">{item.label}</span>
                <span>{item.value}</span>
              </span>
            ))}
            {routingError ? (
              <span className={`inline-flex min-w-0 items-center rounded-full border border-[rgba(var(--brand-700),0.28)] bg-[rgba(var(--brand-700),0.1)] font-medium text-[rgb(var(--brand-700))] ${compactModeEnabled ? 'px-1.5 py-0 text-[10px]' : 'px-2 py-0.5'}`}>
                <span className="max-w-[240px] truncate">{routingError}</span>
              </span>
            ) : null}
          </div>
        </div>
      </section>

      <form
        ref={formRef}
        id="emision-guia-form"
        onSubmit={(e) => {
          e.preventDefault();
          handleEmitirGuia('form.onSubmit');
        }}
        onKeyDownCapture={handleEnterNavigation}
        className={`flex min-h-0 flex-1 flex-col overflow-hidden ${compactModeEnabled ? 'gap-1' : 'gap-1.5'}`}
      >
        <section className={`grid grid-cols-12 overflow-hidden rounded-2xl border border-[rgba(var(--border),0.55)] bg-[rgb(var(--panel))] shadow-[0_12px_28px_rgba(var(--shadow),0.08)] ${compactModeEnabled ? 'gap-2 p-2' : 'gap-2.5 p-3'} [&_input]:min-w-0 [&_input]:w-full [&_input]:max-h-8 [&_input]:h-8 [&_input]:px-2 [&_input]:py-1 [&_input]:text-[12px] [&_label]:text-[10px] [&_label]:leading-tight [&_button]:min-w-0 [&_select]:max-h-8 [&_select]:h-8 [&_select]:px-2 [&_select]:py-1 [&_select]:text-[12px]`}>
          {!isCompactHeight ? (
            <div className="col-span-12 flex items-center justify-between gap-2">
              <h2 className="text-[13px] font-semibold leading-none">Datos operativos</h2>
              {!routingError && routingPreview ? <span className="text-[11px] font-medium text-[rgba(var(--muted),0.92)]">Routing operativo resuelto</span> : null}
            </div>
          ) : null}

          <div className={`col-span-12 grid min-w-0 grid-cols-12 ${compactModeEnabled ? 'gap-x-2 gap-y-1.5' : 'gap-x-2.5 gap-y-2'}`}>
            <div className="col-span-12 min-w-0 lg:col-span-3 xl:col-span-4"><Field label="Línea" hint={isCompactHeight ? undefined : 'CÓDIGO - ORIGEN / DESTINO'} error={errors.linea_numero?.message} required>
              <div ref={quickLineaRef} className="min-w-0">
                <Combobox
                  value={watch('linea_numero')}
                  onChange={value => {
                    const lineaSeleccionada = lineasData?.lineas?.find(item => String(item.id) === String(value));
                    console.log('seleccionarLinea', lineaSeleccionada || value);
                    setValue('linea_numero', value, { shouldValidate: true });
                  }}
                  options={lineasOptions}
                  placeholder="Seleccione línea"
                  renderInPortal
                />
              </div>
            </Field></div>

            <div className="col-span-12 min-w-0 md:col-span-4 lg:col-span-2"><Field label="Cobro" htmlFor="modalidad_cobro" error={errors.modalidad_cobro?.message} required>
              <Select
                id="modalidad_cobro"
                value={watch('modalidad_cobro') || ''}
                onChange={event => setValue('modalidad_cobro', event.target.value, { shouldValidate: true })}
              >
                <option value="">Seleccione modalidad</option>
                {modalidadOptions.map(item => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </Select>
            </Field></div>

            <div className="col-span-12 min-w-0 md:col-span-3 lg:col-span-2">
              <Field label="Pagador">
                <button
                  type="button"
                  onClick={() => setShowClientePagador(prev => !prev)}
                  className="flex h-8 w-full items-center justify-between rounded-xl border border-[rgba(var(--border),0.5)] bg-[rgba(var(--subpanel),0.25)] px-2 text-left text-[12px]"
                >
                  <span className="truncate">{showClientePagador ? 'Ocultar selector' : (pagadorNombre || 'Mostrar selector')}</span>
                  <span className="text-[10px] text-[rgba(var(--muted),0.9)]">F8</span>
                </button>
              </Field>
            </div>

            <div className="col-span-12 min-w-0 md:col-span-5 lg:col-span-3"><Field label="Tipo entrega" htmlFor="tipo_entrega" error={errors.tipo_entrega?.message} required>
              <Select
                id="tipo_entrega"
                value={watch('tipo_entrega') || ''}
                onChange={event => setValue('tipo_entrega', event.target.value, { shouldValidate: true })}
              >
                <option value="">Seleccione tipo de entrega</option>
                {entregaOptions.map(item => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </Select>
            </Field></div>

            <div className="col-span-12 min-w-0 md:col-span-3 lg:col-span-2 xl:col-span-1"><Field label="Contrareembolso" error={errors.contrareembolso?.message}>
              <NumberInput
                id="contrareembolso"
                value={watch('contrareembolso')}
                onValueChange={value => setValue('contrareembolso', Number(value || 0), { shouldValidate: true })}
              />
            </Field></div>
          </div>

          <div className={`col-span-12 grid min-w-0 grid-cols-12 items-start ${compactModeEnabled ? 'gap-2' : 'gap-2.5'}`}>
            <div className={`col-span-12 grid min-w-0 grid-cols-12 ${compactModeEnabled ? 'gap-2' : 'gap-2.5'}`}>
            <div ref={remitenteRef} className={`col-span-12 min-w-0 rounded-2xl border border-[rgba(var(--border),0.45)] bg-[rgba(var(--subpanel),0.18)] ${compactModeEnabled ? 'p-2.5' : 'p-3.5'} xl:col-span-6`}><div className={`flex items-center justify-between gap-2 ${compactModeEnabled ? 'mb-2' : 'mb-2.5'}`}><div className="min-w-0"><h3 className={`${compactModeEnabled ? 'text-[13px]' : 'text-sm'} font-semibold`}>Remitente <span className="ml-1 text-xs text-[rgb(var(--brand))]">*</span></h3>{!isCompactHeight ? <p className="mt-0.5 text-[10px] text-[rgba(var(--muted),0.78)]">Origen del envío</p> : null}</div><span className="max-w-[46%] truncate text-[10px] text-[rgba(var(--muted),0.92)]">{remitenteNombre}</span></div><Field error={errors.remitente_id?.message}>
              <ClienteSelectWithModal
                id="remitente_id"
                allowEventualDni
                name="remitente_id"
                value={watch('remitente_id')}
                selectedCliente={findClienteById(watch('remitente_id'))}
                onChange={e => setValue('remitente_id', e.target.value, { shouldValidate: true })}
                onClienteSelect={handleRemitenteSelect}
                placeholder="Buscar remitente"
                disabled={fetchingClientes}
                excludeId={watch('destinatario_id')}
              />
            </Field>

            <div className="col-span-12 min-w-0 overflow-hidden rounded-xl border border-[rgba(var(--border),0.45)] bg-[rgba(var(--subpanel),0.35)]">
              <button
                type="button"
                aria-expanded={showRemitenteSnapshot}
                onClick={() => setShowRemitenteSnapshot(prev => !prev)}
                className={`flex w-full min-w-0 items-center justify-between text-left font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))] ${compactModeEnabled ? 'h-7 px-2 text-[10px]' : 'h-8 px-2.5 text-[11px]'}`}
              >
                <span>Editar datos de envío</span>
                <ChevronDown size={16} className={`transition-transform ${showRemitenteSnapshot ? 'rotate-180' : ''}`} />
              </button>
              <div className={`grid transition-all duration-200 ${showRemitenteSnapshot ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
                <div className="overflow-hidden">
                <div className={`grid min-w-0 grid-cols-12 border-t border-[rgba(var(--border),0.32)] ${compactModeEnabled ? 'gap-1.5 p-2' : 'gap-2 p-2.5'}`}>
                  <div className="col-span-12 min-w-0">
                    <Field label="Dirección" htmlFor="domicilio_remitente">
                      <Input id="domicilio_remitente" {...register('domicilio_remitente')} placeholder="Calle y altura" />
                    </Field>
                  </div>
                  <div className="col-span-12 min-w-0 md:col-span-6">
                    <Field label="Provincia" htmlFor="provincia_remitente">
                      <Select
                        id="provincia_remitente"
                        value={remitenteProvinciaId}
                        onChange={event => applyPartyGeoSelection('remitente', event.target.value)}
                      >
                        <option value="">Seleccione provincia</option>
                        {provinceOptions.map(option => (
                          <option key={option.id} value={option.id}>
                            {option.nombre}
                          </option>
                        ))}
                      </Select>
                    </Field>
                  </div>
                  <div className="col-span-12 min-w-0 md:col-span-6">
                    <Field label="Localidad" htmlFor="localidad_remitente">
                      <Select
                        id="localidad_remitente"
                        value={remitenteLocalidadId}
                        onChange={event => handlePartyLocalidadChange('remitente', event.target.value)}
                        disabled={!remitenteProvinciaId}
                      >
                        <option value="">{remitenteProvinciaId ? 'Seleccione localidad' : 'Seleccione provincia primero'}</option>
                        {remitenteLocalidadOptions.map(option => (
                          <option key={option.id} value={option.id}>
                            {option.nombre}
                          </option>
                        ))}
                      </Select>
                    </Field>
                  </div>
                  <div className="col-span-12 min-w-0 md:col-span-6">
                    <Field label="Teléfono" htmlFor="telefono_remitente">
                      <Input id="telefono_remitente" {...register('telefono_remitente')} placeholder="Teléfono" />
                    </Field>
                  </div>
                  <div className="col-span-12 min-w-0 md:col-span-6">
                    <Field label="Contacto" htmlFor="contacto_remitente">
                      <Input id="contacto_remitente" {...register('contacto_remitente')} placeholder="Nombre de contacto" />
                    </Field>
                  </div>
                </div>
                </div>
              </div>
            </div></div>

            <div ref={destinatarioRef} className={`col-span-12 min-w-0 rounded-2xl border border-[rgba(var(--border),0.45)] bg-[rgba(var(--subpanel),0.18)] ${compactModeEnabled ? 'p-2.5' : 'p-3.5'} xl:col-span-6`}><div className={`flex items-center justify-between gap-2 ${compactModeEnabled ? 'mb-2' : 'mb-2.5'}`}><div className="min-w-0"><h3 className={`${compactModeEnabled ? 'text-[13px]' : 'text-sm'} font-semibold`}>Destinatario <span className="ml-1 text-xs text-[rgb(var(--brand))]">*</span></h3>{!isCompactHeight ? <p className="mt-0.5 text-[10px] text-[rgba(var(--muted),0.78)]">Destino de la entrega</p> : null}</div><span className="max-w-[46%] truncate text-[10px] text-[rgba(var(--muted),0.92)]">{destinatarioNombre}</span></div><Field error={errors.destinatario_id?.message}>
              <ClienteSelectWithModal
                id="destinatario_id"
                allowEventualDni
                name="destinatario_id"
                value={watch('destinatario_id')}
                selectedCliente={findClienteById(watch('destinatario_id'))}
                onChange={e => setValue('destinatario_id', e.target.value, { shouldValidate: true })}
                onClienteSelect={handleDestinatarioSelect}
                placeholder="Buscar destinatario"
                disabled={fetchingClientes}
                excludeId={watch('remitente_id')}
              />
            </Field>

            <div className="col-span-12 min-w-0 overflow-hidden rounded-xl border border-[rgba(var(--border),0.45)] bg-[rgba(var(--subpanel),0.35)]">
              <button
                type="button"
                aria-expanded={showDestinatarioSnapshot}
                onClick={() => setShowDestinatarioSnapshot(prev => !prev)}
                className={`flex w-full min-w-0 items-center justify-between text-left font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))] ${compactModeEnabled ? 'h-7 px-2 text-[10px]' : 'h-8 px-2.5 text-[11px]'}`}
              >
                <span>Editar datos de envío</span>
                <ChevronDown size={16} className={`transition-transform ${showDestinatarioSnapshot ? 'rotate-180' : ''}`} />
              </button>
              <div className={`grid transition-all duration-200 ${showDestinatarioSnapshot ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
                <div className="overflow-hidden">
                <div className={`grid min-w-0 grid-cols-12 border-t border-[rgba(var(--border),0.32)] ${compactModeEnabled ? 'gap-1.5 p-2' : 'gap-2 p-2.5'}`}>
                  <div className="col-span-12 min-w-0">
                    <Field label="Dirección" htmlFor="domicilio_destinatario">
                      <Input id="domicilio_destinatario" {...register('domicilio_destinatario')} placeholder="Calle y altura" />
                    </Field>
                  </div>
                  <div className="col-span-12 min-w-0 md:col-span-6">
                    <Field label="Provincia" htmlFor="provincia_destinatario">
                      <Select
                        id="provincia_destinatario"
                        value={destinatarioProvinciaId}
                        onChange={event => applyPartyGeoSelection('destinatario', event.target.value)}
                      >
                        <option value="">Seleccione provincia</option>
                        {provinceOptions.map(option => (
                          <option key={option.id} value={option.id}>
                            {option.nombre}
                          </option>
                        ))}
                      </Select>
                    </Field>
                  </div>
                  <div className="col-span-12 min-w-0 md:col-span-6">
                    <Field label="Localidad" htmlFor="localidad_destinatario">
                      <Select
                        id="localidad_destinatario"
                        value={destinatarioLocalidadId}
                        onChange={event => handlePartyLocalidadChange('destinatario', event.target.value)}
                        disabled={!destinatarioProvinciaId}
                      >
                        <option value="">{destinatarioProvinciaId ? 'Seleccione localidad' : 'Seleccione provincia primero'}</option>
                        {destinatarioLocalidadOptions.map(option => (
                          <option key={option.id} value={option.id}>
                            {option.nombre}
                          </option>
                        ))}
                      </Select>
                    </Field>
                  </div>
                  <div className="col-span-12 min-w-0 md:col-span-6">
                    <Field label="Teléfono" htmlFor="telefono_destinatario">
                      <Input id="telefono_destinatario" {...register('telefono_destinatario')} placeholder="Teléfono" />
                    </Field>
                  </div>
                  <div className="col-span-12 min-w-0 md:col-span-6">
                    <Field label="Contacto" htmlFor="contacto_destinatario">
                      <Input id="contacto_destinatario" {...register('contacto_destinatario')} placeholder="Nombre de contacto" />
                    </Field>
                  </div>
                </div>
                </div>
              </div>
            </div></div>
            </div>
            {(showClientePagador || subcuentasActivas.length > 0) ? (
              <div className="col-span-12 min-w-0 rounded-2xl border border-[rgba(var(--border),0.45)] bg-[rgba(var(--subpanel),0.18)] p-3">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <h3 className="text-sm font-semibold">Cliente pagador</h3>
                    <p className="mt-0.5 text-[10px] text-[rgba(var(--muted),0.78)]">Se autocompleta según origen/destino y puede ajustarse antes de emitir.</p>
                  </div>
                  <span className="max-w-[46%] truncate text-[10px] text-[rgba(var(--muted),0.92)]">{pagadorNombre}</span>
                </div>
                <Field>
                  <ClienteSelectWithModal
                    id="cliente_pagador_id"
                    name="cliente_pagador_id"
                    value={watch('cliente_pagador_id')}
                    selectedCliente={findClienteById(watch('cliente_pagador_id'))}
                    onChange={e => setValue('cliente_pagador_id', e.target.value, { shouldValidate: false, shouldDirty: true })}
                    onClienteSelect={handlePagadorSelect}
                    placeholder="Buscar cliente pagador"
                    disabled={fetchingClientes}
                  />
                </Field>
                {subcuentasActivas.length > 0 ? (
                  <Field label={`Subcuenta${subcuentasActivas.length > 1 ? ' *' : ''}`} htmlFor="subcuenta_id">
                    <Select id="subcuenta_id" value={subcuentaId || ''} onChange={event => setValue('subcuenta_id', event.target.value, { shouldValidate: true, shouldDirty: true })} disabled={fetchingSubcuentas || subcuentasActivas.length === 1}>
                      {subcuentasActivas.length > 1 ? <option value="">Seleccione una subcuenta</option> : null}
                      {subcuentasActivas.map(subcuenta => <option key={subcuenta.id} value={subcuenta.id}>{subcuenta.codigo} · {subcuenta.nombre}</option>)}
                    </Select>
                  </Field>
                ) : null}
              </div>
            ) : null}
          </div>
        </section>

        {hasPermission(user, 'RETORNABLES_VER') ? (
          <details className="rounded-2xl border border-[rgba(var(--border),0.45)] bg-[rgba(var(--panel),0.94)] px-3 py-2">
            <summary className="cursor-pointer text-[12px] font-semibold">
              Retornables {retornables.length ? `· ${retornables.reduce((sum, item) => sum + Number(item.cantidad || 0), 0)} unidades` : ''}
            </summary>
            <div className="mt-2 grid grid-cols-12 gap-2">
              <Select className="col-span-12 h-8 text-xs md:col-span-5" value={retornableDraft.tipo_retornable_id} onChange={event => setRetornableDraft(current => ({ ...current, tipo_retornable_id: event.target.value }))}>
                <option value="">Tipo de retornable</option>
                {retornablesTipos.map(tipo => <option key={tipo.id} value={tipo.id}>{tipo.nombre}{!tipo.controlaSaldo ? ' · informativo' : ''}</option>)}
              </Select>
              <NumberInput className="col-span-6 h-8 md:col-span-2" min={1} step={1} value={retornableDraft.cantidad} onChange={value => setRetornableDraft(current => ({ ...current, cantidad: value }))} placeholder="Cantidad" />
              <Select className="col-span-6 h-8 text-xs md:col-span-3" value={retornableDraft.propietario_tipo} onChange={event => setRetornableDraft(current => ({ ...current, propietario_tipo: event.target.value }))}>
                <option value="CLIENTE">Propiedad cliente</option>
                <option value="TRANSPORTE">Propiedad transporte</option>
                <option value="TERCERO">Propiedad tercero</option>
              </Select>
              <Button type="button" size="sm" variant="outline" className="col-span-12 h-8 md:col-span-2" onClick={() => {
                if (!retornableDraft.tipo_retornable_id || Number(retornableDraft.cantidad) <= 0) return;
                setRetornables(current => [...current, { ...retornableDraft, cantidad: Number(retornableDraft.cantidad), tipo_retornable_id: Number(retornableDraft.tipo_retornable_id) }]);
                setRetornableDraft({ tipo_retornable_id: '', cantidad: '', propietario_tipo: 'CLIENTE' });
              }}>Agregar</Button>
            </div>
            {retornables.length ? <div className="mt-2 flex flex-wrap gap-1.5">{retornables.map((item, index) => {
              const tipo = retornablesTipos.find(option => String(option.id) === String(item.tipo_retornable_id));
              return <span key={`${item.tipo_retornable_id}-${index}`} className="inline-flex items-center gap-1 rounded-full border border-[rgba(var(--border),0.5)] px-2 py-1 text-[11px]">
                {item.cantidad} {tipo?.nombre || 'Retornable'} · {item.propietario_tipo.toLowerCase()}
                <button type="button" aria-label="Quitar retornable" onClick={() => setRetornables(current => current.filter((_, itemIndex) => itemIndex !== index))}><X size={12} /></button>
              </span>;
            })}</div> : null}
          </details>
        ) : null}

        <section className={`flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-[rgba(var(--brand),0.18)] bg-[linear-gradient(180deg,rgba(var(--panel),1),rgba(var(--subpanel),0.2))] shadow-[0_14px_30px_rgba(var(--shadow),0.1)] ${compactModeEnabled ? 'p-1.5' : 'p-2'} [&_input]:min-w-0 [&_input]:w-full [&_input]:max-h-8 [&_input]:h-8 [&_input]:px-2 [&_input]:py-1 [&_input]:text-[12px] [&_label]:text-[10px] [&_label]:leading-tight [&_button]:min-w-0`}>
          <div className={`flex flex-wrap items-center justify-between gap-2 ${compactModeEnabled ? 'mb-1' : 'mb-1.5'}`}>
            <div>
              <h2 className="text-[13px] font-semibold leading-none">Carga de remito</h2>
              {!isCompactHeight ? <p className="mt-0.5 text-[10px] text-[rgba(var(--muted),0.9)]">Alta rápida con foco continuo sobre la planilla.</p> : null}
            </div>
            <div className={`flex flex-wrap items-center font-semibold ${compactModeEnabled ? 'gap-1 text-[10px]' : 'gap-1.5 text-[11px]'}`}>
              <button
                type="button"
                onClick={() => setObservacionesOpen(true)}
                className={`rounded-full bg-[rgba(var(--subpanel),0.75)] text-[rgb(var(--fg))] ${compactModeEnabled ? 'px-1.5 py-0.5' : 'px-2 py-1'}`}
              >
                Observaciones F7
              </button>
              <span className={`rounded-full bg-[rgba(var(--subpanel),0.75)] text-[rgb(var(--fg))] ${compactModeEnabled ? 'px-1.5 py-0.5' : 'px-2 py-1'}`}>Enter avanza/agrega</span>
              {!isCompactHeight ? <span className="rounded-full bg-[rgba(var(--subpanel),0.75)] px-2 py-1 text-[rgb(var(--fg))]">Doble click edita</span> : null}
            </div>
          </div>

          <div className={`grid min-w-0 grid-cols-12 rounded-2xl border border-[rgba(var(--border),0.5)] bg-[rgba(var(--subpanel),0.18)] ${compactModeEnabled ? 'gap-1.5 p-1.5' : 'gap-2 p-2'}`} data-remito-entry="true">
            <div className="col-span-6 min-w-0 md:col-span-2 lg:col-span-1">
              <Field label="Pto vta" error={remitoErrors.numero_remito} hint={isCompactHeight ? undefined : '4 dígitos'}>
                <Input
                  ref={remitoPuntoVentaInputRef}
                  id="remito_punto_venta"
                  inputMode="numeric"
                  placeholder="0000"
                  value={remitoDraft.punto_venta}
                  onChange={e => handlePuntoVentaChange(e.target.value)}
                  onBlur={handleRemitoDraftBlur}
                />
              </Field>
            </div>
            <div className="col-span-6 min-w-0 md:col-span-4 lg:col-span-2">
              <Field label="Número de remito" error={remitoErrors.numero_remito} hint={isCompactHeight ? undefined : '8 dígitos'}>
                <Input
                  ref={remitoNumeroInputRef}
                  id="numero_remito"
                  inputMode="numeric"
                  placeholder="00000000"
                  value={remitoDraft.numero_remito}
                  onChange={e => handleNumeroRemitoChange(e.target.value)}
                  onBlur={handleRemitoDraftBlur}
                />
              </Field>
            </div>
            {[
              { key: 'bultos', label: 'Bultos', className: 'col-span-4 min-w-0 md:col-span-2 lg:col-span-1' },
              { key: 'kg_reales', label: 'Kg reales', className: 'col-span-4 min-w-0 md:col-span-2 lg:col-span-1' },
              { key: 'm3', label: 'M3', className: 'col-span-4 min-w-0 md:col-span-2 lg:col-span-1' },
              { key: 'kg_aforado', label: 'Kg aforado', className: 'col-span-6 min-w-0 md:col-span-2 lg:col-span-1' },
              { key: 'valor_declarado', label: 'Valor declarado', className: 'col-span-6 min-w-0 md:col-span-3 lg:col-span-2' },
            ].map(field => (
              <div key={field.key} className={field.className}>
                <NumberInput id={field.key} label={field.label} value={remitoDraft[field.key]} onValueChange={value => handleDraftChange(field.key, value)} error={remitoErrors[field.key]} />
              </div>
            ))}
            <div className="col-span-12 min-w-0 md:col-span-9 lg:col-span-3">
              <Field label="Descripción" error={remitoErrors.descripcion}>
                <>
                  <Input
                    id="remito_descripcion"
                    list="remito-descripcion-options"
                    value={remitoDraft.descripcion}
                    onChange={e => handleDraftChange('descripcion', e.target.value)}
                    placeholder="Bultos, cajas, pallets o texto libre"
                  />
                  <datalist id="remito-descripcion-options">
                    {remitoDescriptionOptions.map(option => (
                      <option key={option} value={option} />
                    ))}
                  </datalist>
                </>
              </Field>
            </div>
          </div>

          <div className="col-span-12 flex items-end justify-end gap-1.5">
            <Button type="button" size="sm" variant="outline" onClick={handleOcr} className={`${compactModeEnabled ? 'h-7 px-2 text-[10px]' : 'h-8 px-2 text-[11px]'}`}><ScanText size={14} /> Leer remito</Button>
            <Button type="button" size="sm" variant="strong" onClick={handleAddRemito} className={`${compactModeEnabled ? 'h-7 px-2 text-[10px]' : 'h-8 px-2 text-[11px]'}`}><Plus size={14} /> Agregar remito</Button>
          </div>

          <div className={`flex min-h-0 flex-1 overflow-hidden ${compactModeEnabled ? 'mt-1' : 'mt-1.5'}`}>
            <div className={`h-full min-h-0 w-full overflow-y-auto overflow-x-hidden rounded-2xl border border-[rgba(var(--brand),0.18)] bg-[rgba(var(--panel),0.98)] shadow-[inset_0_1px_0_rgba(var(--border),0.22)] ${isCompactHeight ? 'max-h-[calc(100vh-390px)]' : 'max-h-[calc(100vh-430px)]'}`}>
            <table className={`min-w-full table-fixed text-[rgb(var(--fg))] ${compactModeEnabled ? 'text-[11px]' : 'text-xs'}`}>
              <thead className={`sticky top-0 z-10 bg-[rgba(var(--subpanel),0.96)] uppercase tracking-[0.08em] text-[rgba(var(--muted),0.95)] ${compactModeEnabled ? 'text-[9px]' : 'text-[10px]'}`}>
                <tr>
                  <th className={`w-10 text-left ${compactModeEnabled ? 'px-1.5 py-1' : 'px-2 py-1.5'}`}>#</th>
                  <th className={`w-20 text-left ${compactModeEnabled ? 'px-1.5 py-1' : 'px-2 py-1.5'}`}>Pto</th>
                  <th className={`w-28 text-left ${compactModeEnabled ? 'px-1.5 py-1' : 'px-2 py-1.5'}`}>Remito</th>
                  <th className={`w-16 text-right ${compactModeEnabled ? 'px-1.5 py-1' : 'px-2 py-1.5'}`}>Bultos</th>
                  <th className={`w-20 text-right ${compactModeEnabled ? 'px-1.5 py-1' : 'px-2 py-1.5'}`}>Kg</th>
                  <th className={`w-16 text-right ${compactModeEnabled ? 'px-1.5 py-1' : 'px-2 py-1.5'}`}>M3</th>
                  <th className={`w-20 text-right ${compactModeEnabled ? 'px-1.5 py-1' : 'px-2 py-1.5'}`}>Aforado</th>
                  <th className={`w-24 text-right ${compactModeEnabled ? 'px-1.5 py-1' : 'px-2 py-1.5'}`}>Val. decl.</th>
                  <th className={`text-left ${compactModeEnabled ? 'px-1.5 py-1' : 'px-2 py-1.5'}`}>Descripción</th>
                  <th className={`w-28 text-right ${compactModeEnabled ? 'px-1.5 py-1' : 'px-2 py-1.5'}`}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {!remitosArray.fields.length && (
                  <tr><td colSpan={10} className={isCompactHeight ? 'p-2' : 'p-3'}><EmptyState title="Sin remitos" description="Agregue al menos un remito para emitir la guía." /></td></tr>
                )}
                {remitosArray.fields.map((remito, index) => {
                  const remitoParts = splitRemitoNumber(remito.numero_remito);
                  const isEditingRow = editingRowIndex === index;
                  const isActiveRow = activeRemitoId === remito.id || isEditingRow;
                  const rowTone = isEditingRow
                    ? 'bg-[rgba(var(--brand),0.16)] ring-1 ring-inset ring-[rgba(var(--brand),0.32)]'
                    : highlightRemitoId === remito.id
                      ? 'bg-[rgba(var(--brand),0.12)] transition-colors duration-700'
                      : isActiveRow
                        ? 'bg-[rgba(var(--brand),0.08)]'
                        : 'odd:bg-[rgba(var(--panel),0.72)] even:bg-[rgba(var(--subpanel),0.16)]';
                  return (
                  <tr
                    key={remito.id}
                    tabIndex={0}
                    className={`cursor-default border-t border-[rgba(var(--border),0.28)] outline-none ${rowTone}`}
                    onClick={() => setActiveRemitoId(remito.id)}
                    onFocus={() => setActiveRemitoId(remito.id)}
                    onDoubleClick={() => startEditingRemito(index, remito)}
                  >
                    <td className={`align-top font-semibold ${compactModeEnabled ? 'px-1.5 py-1' : 'px-2 py-1.5'}`}>
                      <span className={`inline-flex items-center justify-center rounded-full ${compactModeEnabled ? 'min-h-5 min-w-5 px-1 text-[9px]' : 'min-h-6 min-w-6 px-1.5'} ${isActiveRow ? 'bg-[rgba(var(--brand),0.16)] text-[rgb(var(--brand))]' : 'bg-[rgba(var(--subpanel),0.9)] text-[rgb(var(--fg))]'}`}>{index + 1}</span>
                    </td>
                    <td className={`align-top ${compactModeEnabled ? 'px-1.5 py-1' : 'px-2 py-1.5'}`}>{remitoParts.puntoVenta || '0000'}</td>
                    <td className={`align-top font-semibold ${compactModeEnabled ? 'px-1.5 py-1' : 'px-2 py-1.5'}`}>
                      {isEditingRow ? <Input value={editingRemitoDraft.numero_remito} onChange={e => handleEditingDraftChange('numero_remito', e.target.value)} /> : (remitoParts.numero || remito.numero_remito)}
                    </td>
                    <td className={`text-right align-top ${compactModeEnabled ? 'px-1.5 py-1' : 'px-2 py-1.5'}`}>
                      {isEditingRow ? <Input value={editingRemitoDraft.bultos} onChange={e => handleEditingDraftChange('bultos', e.target.value)} /> : remito.bultos}
                    </td>
                    <td className={`text-right align-top ${compactModeEnabled ? 'px-1.5 py-1' : 'px-2 py-1.5'}`}>
                      {isEditingRow ? <Input value={editingRemitoDraft.kg_reales} onChange={e => handleEditingDraftChange('kg_reales', e.target.value)} /> : remito.kg_reales}
                    </td>
                    <td className={`text-right align-top ${compactModeEnabled ? 'px-1.5 py-1' : 'px-2 py-1.5'}`}>
                      {isEditingRow ? <Input value={editingRemitoDraft.m3} onChange={e => handleEditingDraftChange('m3', e.target.value)} /> : remito.m3}
                    </td>
                    <td className={`text-right align-top ${compactModeEnabled ? 'px-1.5 py-1' : 'px-2 py-1.5'}`}>
                      {isEditingRow ? <Input value={editingRemitoDraft.kg_aforado} onChange={e => handleEditingDraftChange('kg_aforado', e.target.value)} /> : remito.kg_aforado}
                    </td>
                    <td className={`text-right align-top ${compactModeEnabled ? 'px-1.5 py-1' : 'px-2 py-1.5'}`}>
                      {isEditingRow ? <Input value={editingRemitoDraft.valor_declarado} onChange={e => handleEditingDraftChange('valor_declarado', e.target.value)} /> : formatCurrency(remito.valor_declarado)}
                    </td>
                    <td className={`align-top ${compactModeEnabled ? 'px-1.5 py-1' : 'px-2 py-1.5'}`}>
                      {isEditingRow ? (
                        <Input value={editingRemitoDraft.descripcion} onChange={e => handleEditingDraftChange('descripcion', e.target.value)} />
                      ) : (
                        <span className="block truncate" title={remito.descripcion}>{remito.descripcion || 'Sin descripción'}</span>
                      )}
                    </td>
                    <td className={`text-right align-top ${compactModeEnabled ? 'px-1.5 py-1' : 'px-2 py-1.5'}`}>
                      <div className={`inline-flex ${compactModeEnabled ? 'gap-0.5' : 'gap-1'}`}>
                        {isEditingRow ? (
                          <>
                            <Button type="button" size="sm" variant="ghost" className={compactModeEnabled ? 'h-7 px-2 text-[10px]' : 'h-8 px-2 text-[11px]'} onClick={saveEditingRemito}>Guardar</Button>
                            <Button type="button" size="sm" variant="ghost" className={compactModeEnabled ? 'h-7 px-2 text-[10px]' : 'h-8 px-2 text-[11px]'} onClick={cancelEditingRemito}>Cancelar</Button>
                          </>
                        ) : (
                          <Button type="button" size="sm" variant="ghost" className={compactModeEnabled ? 'h-7 px-2' : 'h-8 px-2'} onClick={() => startEditingRemito(index, remito)}><Pencil size={14} /></Button>
                        )}
                        <Button type="button" size="sm" variant="ghost" className={compactModeEnabled ? 'h-7 px-2' : 'h-8 px-2'} onClick={() => {
                          const copy = { ...remito, numero_remito: `${remito.numero_remito}-C` };
                          const result = addRemito(copy);
                          if (!result.ok) setRemitoErrors(result.errors || {});
                        }}><CopyPlus size={14} /></Button>
                        <Button type="button" size="sm" variant="ghost" className={compactModeEnabled ? 'h-7 px-2' : 'h-8 px-2'} onClick={async () => {
                          const decision = await Swal.fire({ title: 'Eliminar remito?', icon: 'question', showCancelButton: true });
                          if (decision.isConfirmed) {
                            if (activeRemitoId === remito.id) setActiveRemitoId(null);
                            removeRemito(index);
                          }
                        }}><Trash2 size={14} /></Button>
                      </div>
                    </td>
                  </tr>
                );})}
              </tbody>
            </table>
            </div>
          </div>
        </section>
      </form>

      <div className={`pointer-events-none fixed bottom-3 right-3 z-30 flex items-center gap-2 ${compactModeEnabled ? 'flex-col-reverse items-end' : ''}`}>
        <Button type="button" variant="outline" size="sm" className="pointer-events-auto h-8 px-3 text-[11px] shadow-[0_10px_22px_rgba(var(--shadow),0.18)]" onClick={handleCancelarEmision} disabled={isSubmitting || isEmitiendo}>
          Cancelar
        </Button>
        <Button
          type="button"
          variant="primary"
          size="sm"
          className="pointer-events-auto h-9 px-3 text-[11px] shadow-[0_12px_28px_rgba(var(--shadow),0.24)]"
          disabled={isSubmitting || isEmitiendo || !canEmitir}
          onClick={() => {
            const formElement = document.getElementById('emision-guia-form');
            if (formElement?.requestSubmit) {
              formElement.requestSubmit();
              return;
            }
            handleEmitirGuia('stickyBar.onSubmit:fallback');
          }}
        >
          {isEmitiendo ? 'Emitiendo...' : 'Emitir Guía'}
        </Button>
      </div>
      </div>

      <OperatorModePalette operatorMode={operatorMode} />

      {observacionesOpen ? (
        <div className="fixed inset-0 z-[var(--z-modal)] flex items-center justify-center bg-[rgba(var(--shadow),0.42)] p-4">
          <div className="w-full max-w-2xl rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgb(var(--panel))] p-4 shadow-[0_24px_60px_rgba(var(--shadow),0.28)]">
            <div className="mb-3 flex items-center justify-between gap-3">
              <div>
                <h3 className="text-base font-semibold">Observaciones</h3>
                <p className="text-xs text-[rgba(var(--muted),0.88)]">Notas operativas fuera del flujo principal.</p>
              </div>
              <Button type="button" variant="ghost" size="icon" onClick={() => setObservacionesOpen(false)}>
                <X size={16} />
              </Button>
            </div>

            <Textarea
              id="observaciones_modal"
              rows={7}
              {...register('observaciones')}
              placeholder="Notas operativas..."
              className="resize-none"
            />

            <div className="mt-4 flex items-center justify-between gap-3 text-xs text-[rgba(var(--muted),0.88)]">
              <span>Esc - cerrar</span>
              <Button type="button" variant="outline" onClick={() => setObservacionesOpen(false)}>Cerrar</Button>
            </div>
          </div>
        </div>
      ) : null}

      {modalValorizacionOpen && (
        <div className="fixed inset-0 z-[var(--z-modal)] flex items-center justify-center bg-[rgba(var(--shadow),0.45)] p-4 sm:p-6">
          <div
            className="flex w-[min(1100px,calc(100vw-48px))] max-h-[calc(100vh-48px)] flex-col overflow-hidden rounded-[28px] border border-[rgba(var(--border),0.6)] bg-[rgb(var(--panel))] shadow-[0_24px_60px_rgba(var(--shadow),0.3)]"
          >
            <div className="border-b border-[rgba(var(--border),0.45)] px-4 py-3 sm:px-5 sm:py-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-lg font-semibold">Valorización</h3>
                    <span className={`inline-flex min-h-7 items-center rounded-full border px-2.5 text-[11px] font-semibold tracking-[0.12em] ${valuationStatus.className}`}>
                      {valuationStatus.label}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-[rgba(var(--muted),0.88)]">
                    La tarifa, el routing y los snapshots de remitente, destinatario y pagador quedarán congelados al confirmar.
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="text-right">
                    <p className="text-[11px] uppercase tracking-[0.14em] text-[rgba(var(--muted),0.78)]">Total</p>
                    <p className="text-xl font-semibold leading-none text-[rgb(var(--fg))]">
                      {formatCurrency(resolvedEmissionPayload?.total_valorizado || tarifaPreview?.importe_sugerido || 0)}
                    </p>
                  </div>
                  <button type="button" onClick={() => setModalValorizacionOpen(false)} className="rounded-lg p-1 focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"><X size={16} /></button>
                </div>
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-hidden">
              <div className="flex h-full flex-col overflow-hidden">
                <div className="border-b border-[rgba(var(--border),0.35)] px-4 py-2.5 text-xs text-[rgba(var(--fg),0.85)] sm:px-5">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span className="font-medium">Tarifa sugerida: <strong>{formatCurrency(tarifaPreview?.importe_sugerido || 0)}</strong></span>
                    {tarifaPreviewWarnings.length ? (
                      <span className="inline-flex min-h-7 items-center rounded-full border border-[rgba(var(--warning),0.3)] bg-[rgba(var(--warning),0.12)] px-2.5 text-[rgb(var(--warning))]">
                        {tarifaPreviewWarnings.join(' ')}
                      </span>
                    ) : null}
                  </div>
                </div>
                <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-5">
                  <div className="grid min-h-0 gap-4 min-[900px]:grid-cols-[minmax(0,1.1fr)_minmax(320px,0.9fr)]">
                    <div className="min-h-0 rounded-2xl border border-[rgba(var(--border),0.45)] bg-[rgba(var(--subpanel),0.18)] p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <h4 className="text-sm font-semibold">Resumen operativo</h4>
                          <p className="mt-1 text-xs text-[rgba(var(--muted),0.82)]">Datos clave congelados para la emisión.</p>
                        </div>
                        {routingPreview?.empresa_redespacho_nombre ? (
                          <span className="rounded-full border border-[rgba(var(--border),0.45)] px-2 py-1 text-[11px] text-[rgba(var(--fg),0.72)]">
                            Redespacho: {routingPreview.empresa_redespacho_nombre}
                          </span>
                        ) : null}
                      </div>

                      <div className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                        {valuationSummaryItems.map(item => (
                          <div
                            key={item.label}
                            className={`rounded-xl border border-[rgba(var(--border),0.42)] bg-[rgba(var(--panel),0.76)] px-3 py-2 ${item.wide ? 'sm:col-span-2 xl:col-span-1' : ''}`}
                          >
                            <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[rgba(var(--muted),0.76)]">{item.label}</p>
                            <p className="mt-1 truncate text-sm font-semibold text-[rgb(var(--fg))]">{item.value}</p>
                          </div>
                        ))}
                      </div>

                      <div className="mt-4 rounded-2xl border border-[rgba(var(--border),0.45)] bg-[rgba(var(--panel),0.74)] p-3">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div>
                            <h4 className="text-sm font-semibold">Decisión tarifaria</h4>
                            <p className="mt-1 text-xs text-[rgba(var(--muted),0.82)]">Seleccione la modalidad antes de confirmar.</p>
                          </div>
                          {tarifaVersionesCandidatas.length > 1 ? (
                            <Button
                              type="button"
                              variant={showAlternativeTariffs ? 'primary' : 'outline'}
                              onClick={() => setShowAlternativeTariffs(prev => !prev)}
                            >
                              Elegir otra tarifa/regla
                            </Button>
                          ) : null}
                        </div>

                        {showAlternativeTariffs && tarifaVersionesCandidatas.length > 1 ? (
                          <div className="mt-3 grid gap-2 lg:grid-cols-[minmax(0,1fr)_auto]">
                            <Select
                              value={selectedTariffVersionId}
                              onChange={event => {
                                setSelectedTariffVersionId(event.target.value);
                                setValuationMode(VALUATION_MODE.AUTO);
                              }}
                              className="h-10"
                            >
                              {tarifaVersionesCandidatas.map(version => (
                                <option key={version.id} value={String(version.id)}>
                                  {formatVersionOptionLabel(version)}
                                </option>
                              ))}
                            </Select>
                            <div className="rounded-xl border border-[rgba(var(--border),0.45)] px-3 py-2 text-xs text-[rgba(var(--fg),0.74)]">
                              Aplicando: <strong>{formatVersionOptionLabel(selectedTariffVersion)}</strong>
                            </div>
                          </div>
                        ) : null}

                        {(valuationMode === VALUATION_MODE.MANUAL || valuationMode === VALUATION_MODE.OVERRIDE) ? (
                          <div className="mt-3 grid gap-3 lg:grid-cols-2">
                            <Field label="Importe manual">
                              <NumberInput
                                value={manualValuationAmount}
                                onChange={value => setManualValuationAmount(value)}
                                min={0}
                                step={0.01}
                              />
                            </Field>
                            <Field label="Modo">
                              <Select
                                value={valuationMode}
                                onChange={event => setValuationMode(event.target.value)}
                                className="h-10"
                              >
                                <option value={VALUATION_MODE.MANUAL}>Manual</option>
                                {canOverrideTariff ? <option value={VALUATION_MODE.OVERRIDE}>Override</option> : null}
                              </Select>
                            </Field>
                            <div className="lg:col-span-2">
                              <Field label="Motivo obligatorio">
                                <Textarea
                                  rows={3}
                                  value={manualValuationReason}
                                  onChange={event => setManualValuationReason(event.target.value)}
                                  placeholder="Explique por qué se carga manualmente o se aplica override."
                                  className="resize-none"
                                />
                              </Field>
                            </div>
                          </div>
                        ) : null}

                        {valuationMode === VALUATION_MODE.SIN_VALORIZAR ? (
                          <div className="mt-3 rounded-xl border border-[rgba(var(--warning),0.3)] bg-[rgba(var(--warning),0.12)] px-3 py-2 text-sm text-[rgb(var(--warning))]">
                            La guía quedará en <strong>PENDIENTE_VALORIZACION</strong>. Facturación y cobranza deben esperar a la valorización posterior.
                          </div>
                        ) : null}
                      </div>
                    </div>

                    <div className="min-h-0 rounded-2xl border border-[rgba(var(--border),0.45)] bg-[rgba(var(--subpanel),0.18)] p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <h4 className="text-sm font-semibold">Desglose tarifario</h4>
                          <p className="mt-1 text-xs text-[rgba(var(--muted),0.82)]">Detalle aplicado a la valorización congelada.</p>
                        </div>
                        <span className="rounded-full border border-[rgba(var(--border),0.45)] px-2 py-1 text-[11px] text-[rgba(var(--fg),0.72)]">
                          {Array.isArray(resolvedEmissionPayload?.detalle_valorizacion_json?.items) ? resolvedEmissionPayload.detalle_valorizacion_json.items.length : 0} ítems
                        </span>
                      </div>

                      {Array.isArray(resolvedEmissionPayload?.detalle_valorizacion_json?.items) && resolvedEmissionPayload.detalle_valorizacion_json.items.length ? (
                        <div className="mt-3 max-h-[260px] overflow-y-auto rounded-xl border border-[rgba(var(--border),0.36)] bg-[rgba(var(--panel),0.82)] p-3">
                          <div className="space-y-2 text-sm text-[rgba(var(--fg),0.82)]">
                            {resolvedEmissionPayload.detalle_valorizacion_json.items.map((item, index) => (
                              <div key={`${item?.detalle || item?.tipo_regla || 'regla'}-${index}`} className="flex items-start justify-between gap-3 rounded-lg border border-[rgba(var(--border),0.18)] px-3 py-2">
                                <div className="min-w-0">
                                  <p className="truncate font-medium">{item?.detalle || item?.tipo_regla || `Regla ${index + 1}`}</p>
                                  <p className="mt-0.5 text-xs text-[rgba(var(--muted),0.72)]">{item?.tipo_regla || item?.codigo || 'REGLA'}</p>
                                </div>
                                <strong className="whitespace-nowrap">{formatCurrency(item?.importe || 0)}</strong>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="mt-3 rounded-xl border border-dashed border-[rgba(var(--border),0.45)] bg-[rgba(var(--panel),0.72)] px-3 py-4 text-sm text-[rgba(var(--muted),0.82)]">
                          No hay desglose tarifario disponible para la decisión actual.
                        </div>
                      )}

                      <div className="mt-3 grid gap-2 sm:grid-cols-2">
                        <div className="rounded-xl border border-[rgba(var(--border),0.36)] bg-[rgba(var(--panel),0.76)] px-3 py-2">
                          <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[rgba(var(--muted),0.76)]">Origen valorización</p>
                          <p className="mt-1 text-sm font-semibold">{resolvedEmissionPayload?.detalle_valorizacion_json?.valuation_decision?.origin || 'automatica'}</p>
                        </div>
                        <div className="rounded-xl border border-[rgba(var(--border),0.36)] bg-[rgba(var(--panel),0.76)] px-3 py-2">
                          <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[rgba(var(--muted),0.76)]">Valorización congelada</p>
                          <p className="mt-1 text-sm font-semibold">{formatCurrency(resolvedEmissionPayload?.total_valorizado || 0)}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {isCalculatingTariff ? (
                  <div className="border-t border-[rgba(var(--border),0.35)] px-4 py-2.5 text-sm text-[rgba(var(--fg),0.85)] sm:px-5">
                    <div className="rounded-xl border border-[rgba(var(--brand),0.24)] bg-[rgba(var(--brand),0.1)] px-3 py-2">
                      Calculando valorización tarifaria...
                    </div>
                  </div>
                ) : null}
                {tarifaPreviewError ? (
                  <div className="border-t border-[rgba(var(--border),0.35)] px-4 py-2.5 text-sm text-[rgb(var(--danger))] sm:px-5">
                    <div className="rounded-xl border border-[rgba(var(--danger),0.28)] bg-[rgba(var(--danger),0.1)] px-3 py-2">
                      {tarifaPreviewError}
                    </div>
                  </div>
                ) : null}
                {valuationDecisionError ? (
                  <div className="border-t border-[rgba(var(--border),0.35)] px-4 py-2.5 text-sm text-[rgb(var(--danger))] sm:px-5">
                    <div className="rounded-xl border border-[rgba(var(--danger),0.28)] bg-[rgba(var(--danger),0.1)] px-3 py-2">
                      {valuationDecisionError}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>

            <div className="sticky bottom-0 border-t border-[rgba(var(--border),0.45)] bg-[rgba(var(--panel),0.96)] px-4 py-3 backdrop-blur-md sm:px-5">
              <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant={valuationMode === VALUATION_MODE.AUTO ? 'primary' : 'outline'}
                    onClick={() => setValuationMode(VALUATION_MODE.AUTO)}
                    disabled={isCalculatingTariff || Boolean(tarifaPreviewError)}
                  >
                    Aplicar sugerida
                  </Button>
                  <Button
                    type="button"
                    variant={valuationMode === VALUATION_MODE.MANUAL || valuationMode === VALUATION_MODE.OVERRIDE ? 'primary' : 'outline'}
                    onClick={() => setValuationMode(canOverrideTariff ? VALUATION_MODE.OVERRIDE : VALUATION_MODE.MANUAL)}
                  >
                    Carga manual / override
                  </Button>
                  {canEmitWithoutValuation ? (
                    <Button
                      type="button"
                      variant={valuationMode === VALUATION_MODE.SIN_VALORIZAR ? 'primary' : 'outline'}
                      onClick={() => setValuationMode(VALUATION_MODE.SIN_VALORIZAR)}
                    >
                      Emitir sin valorizar
                    </Button>
                  ) : null}
                </div>

                <div className="flex flex-wrap justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setModalValorizacionOpen(false)}>Cancelar (F2)</Button>
                  <Button
                    ref={confirmEmitButtonRef}
                    type="button"
                    variant="primary"
                    disabled={isEmitiendo || Boolean(valuationDecisionError) || !resolvedEmissionPayload}
                    onClick={() => emitir(resolvedEmissionPayload)}
                  >
                    Confirmar y emitir (Enter / F5)
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

